/**
 * Registro tipado de TODAS las claves de `settings` (modelo-datos §2.12).
 *
 * Cada clave declara: `scope` (global | branch), esquema Zod, **default del AF**,
 * la `rule` (RN) que la exige y el `pv` (punto a validar) que la ratifica.
 *
 * Regla del proyecto (CLAUDE.md): **nada que sea parámetro se hardcodea** — se lee con
 * `getSetting(key, { branchId })`. Si no hay documento en `settings`, el valor efectivo
 * es el `default` de acá. Los PV-NN no frenan el desarrollo: su default ya funciona.
 *
 * Decisión de nombres: el modelo de datos escribe algunas claves con la sede embebida
 * (`branch.<id>.schedule`, `cash.blindMode.<branchId>`). Acá la sede NO va en la clave:
 * va en `scope: "branch"` + `scope.branchId` del documento, que es el mismo dato mejor
 * ubicado (permite un default global y el override por sede, y hace la clave enumerable).
 * Ver `docs/decisiones/settings-versionados.md`.
 */
import { z } from "zod";
import { FAMILY_DISCOUNT_TARGETS, POS_TERMINAL_MODES } from "./enums.js";
/**
 * Roles que escriben cada familia. Fuente única para la API (`PUT /api/settings/:key` y
 * el calendario de feriados de la grilla) y para el gating de la SPA.
 */
export const SETTING_EDITOR_ROLES = {
    general: ["owner", "intersede_coordinator"],
    financial: ["owner", "admin"],
    owner_only: ["owner"],
    holiday_calendar: ["owner", "admin", "intersede_coordinator", "branch_manager"],
};
/** ¿`role` puede escribir una clave de la familia `editors`? */
export function canEditSetting(role, editors) {
    return SETTING_EDITOR_ROLES[editors].includes(role);
}
const def = (d) => d;
const moneyInt = z.number().int();
const timeHHmm = z.string().regex(/^([01]\d|2[0-3]):[0-5]\d$/, "hora HH:mm");
/** Horario diario de una recepción: `null` = cerrado (AF §1.4). */
const daySchedule = z.union([z.object({ open: timeHHmm, close: timeHHmm }), z.null()]);
const scheduleTable = z.object({
    weekdays: daySchedule,
    saturday: daySchedule,
    sunday: daySchedule,
    holidays: daySchedule,
});
const cashShiftCut = z.object({
    morning: z.object({ from: timeHHmm, to: timeHHmm }),
    afternoon: z.object({ from: timeHHmm, to: timeHHmm }),
    /** Sábados, domingos y feriados operan con un solo turno (RN-CAJ-06, PV-59). */
    singleShiftWeekdays: z.array(z.number().int().min(0).max(6)),
});
const notificationCatalogEntry = z.object({
    active: z.boolean(),
    channels: z.array(z.enum(["push", "email", "inapp"])),
    /** Plazos del aviso; su significado depende del tipo (días antes, cada N días…). */
    params: z.record(z.string(), z.number()).optional(),
    description: z.string(),
});
const securityAlertEntry = z.object({
    active: z.boolean(),
    /** Umbral en % o en centavos, según la alerta; `null` = sin umbral. */
    threshold: z.number().nullable(),
    recipients: z.array(z.string()),
    channels: z.array(z.enum(["push", "email", "inapp"])),
});
const commissionTier = z.object({
    /** % del objetivo alcanzado a partir del cual aplica el tramo. */
    thresholdPct: z.number(),
    /** % de comisión sobre las ventas de planes del mes. */
    commissionPct: z.number(),
});
// ─────────────────────────────────────────────────────────────────────────────
export const SETTINGS_REGISTRY = {
    // ── Acceso (AF §6) ────────────────────────────────────────────────────────
    "access.debtToleranceDays": def({
        scope: "global",
        schema: z.number().int().min(0),
        default: 1,
        rule: "RN-ACC-09 / RN-PLA-09",
        pv: "PV-38",
        editors: "general",
        label: "Días de tolerancia de deuda para entrar",
        description: "Días después del vencimiento de la cuota en que el socio todavía puede entrar.",
        help: "Cuenta los días corridos desde que venció la cuota. Mientras la mora no supere este " +
            "número, el socio entra con luz amarilla y aviso de cuota vencida, y sigue figurando " +
            "como activo; al día siguiente el molinete lo rechaza en todas las sedes por «cuota " +
            "vencida» hasta que pague. Con 1, una cuota que vence el 10 deja entrar el 11 y bloquea " +
            "desde el 12; con 0, deja entrar hasta el mismo 10 y bloquea desde el 11. El mismo " +
            "número decide cuándo una cuota figura como vencida en la cuenta corriente y cuándo un " +
            "socio pasa de activo a moroso: subirlo afloja el cobro en todo el club, no sólo en el " +
            "molinete.",
        unit: "días",
    }),
    "access.externalDebitGraceDays": def({
        scope: "global",
        schema: z.number().int().min(0),
        default: 10,
        rule: "RN-PLA-25 / RN-ACC-09",
        editors: "general",
        label: "Días de espera de la rendición del débito por archivo",
        description: "Días desde el vencimiento en que un adherido al débito por archivo con la cuota ya " +
            "presentada sigue entrando mientras no llega la rendición.",
        help: "El débito por archivo (PrismaNet) no confirma el cobro en el momento: la cuota se " +
            "presenta en el archivo, Maru lo sube y Payway lo procesa; el resultado llega días " +
            "después, cuando se carga la rendición. Mientras tanto la cuota figura «en gestión de " +
            "cobro» y el socio entra normalmente. Este número es cuántos días corridos desde el " +
            "vencimiento se espera esa rendición: pasados, si todavía no llegó, la cuota vuelve a " +
            "contar como impaga para el molinete. Con 10, una cuota que vence el 5 y se presentó " +
            "deja entrar hasta el 15 aunque la rendición no haya llegado; con 0, el socio se " +
            "bloquea como cualquier deudor apenas vence la tolerancia de deuda. Si la rendición " +
            "llega antes, manda la rendición: aceptada salda la cuota, rechazada la deja impaga.",
        unit: "días",
    }),
    "access.aptGraceDays": def({
        scope: "global",
        schema: z.number().int().min(0),
        default: 30,
        rule: "RN-SOC-13 / RN-ACC-10",
        pv: "PV-06 / PV-38",
        editors: "general",
        label: "Días de gracia del apto médico vencido",
        description: "Días que el socio sigue entrando después de que vence su apto médico, antes del bloqueo.",
        help: "Cuando el apto médico vence, empieza a correr esta gracia: el socio sigue entrando con " +
            "luz amarilla y recibe avisos para renovarlo; agotada, el molinete lo rechaza en todas " +
            "las sedes por «apto médico vencido» hasta que suba uno nuevo y recepción lo valide. La " +
            "misma cantidad de días rige para el socio nuevo que todavía no cargó ningún apto, " +
            "contada desde su alta. Con 30, un apto que vence el 1 de marzo sigue dejando entrar " +
            "hasta el 31 de marzo; con 0, bloquea desde el día siguiente al vencimiento. La fecha de " +
            "fin de gracia se fija al validar cada apto: cambiar este valor afecta a los que se " +
            "validen de ahí en adelante y a quienes todavía no tienen apto.",
        unit: "días",
    }),
    "access.qrTtlSec": def({
        scope: "global",
        schema: z.number().int().min(10),
        default: 60,
        rule: "RN-ACC-06",
        pv: "PV-38",
        editors: "general",
        label: "Segundos que dura cada QR del carnet",
        description: "Cada cuántos segundos cambia el QR del carnet digital de la app.",
        help: "El carnet de la app no muestra un QR fijo: genera uno nuevo cada tantos segundos, así " +
            "una captura de pantalla no sirve para entrar más tarde. Este valor es ese ritmo de " +
            "cambio; el molinete acepta además el código inmediatamente anterior y el siguiente, " +
            "para absorber el desfasaje del reloj del teléfono. Con 60, un QR copiado deja de servir " +
            "en uno o dos minutos; con 30 dura la mitad, pero el socio tiene menos margen entre que " +
            "abre la app y lo apoya en el lector. No admite menos de 10. Los teléfonos toman el " +
            "nuevo ritmo cuando la app vuelve a descargar los datos del carnet; hasta entonces, un " +
            "teléfono con el ritmo viejo puede ver rechazado su QR, así que conviene no cambiarlo " +
            "seguido.",
        unit: "segundos",
    }),
    "access.antipassbackSec": def({
        scope: "global",
        schema: z.number().int().min(0),
        default: 90,
        rule: "RN-ACC-09",
        pv: "PV-38",
        editors: "general",
        label: "Segundos para rechazar un QR ya usado",
        description: "Ventana en que una segunda lectura del mismo carnet se rechaza como «QR ya utilizado».",
        help: "Evita que un socio entre y le pase el teléfono al de atrás: si el mismo carnet ya abrió " +
            "el molinete hace menos de estos segundos, la segunda lectura se rechaza con motivo «QR " +
            "ya utilizado» y queda en el registro de ingresos. Sólo cuenta una lectura anterior " +
            "aceptada: un rechazo no le traba el reintento a la misma persona. Con 90, quien pasó a " +
            "las 18:00:00 no puede volver a pasar con el QR hasta las 18:01:30; con 0, el control se " +
            "apaga. Si el molinete no giró y fue un error genuino, recepción lo resuelve con la " +
            "habilitación manual. Aplica a las lecturas del QR; la huella no pasa por este control.",
        unit: "segundos",
    }),
    "access.noReceptionWindowMin": def({
        scope: "global",
        schema: z.number().int().min(0),
        default: 30,
        rule: "AF §6 caso borde",
        pv: "PV-40",
        editors: "general",
        label: "Minutos de apertura previa en locales sin recepción",
        description: "Cuántos minutos antes de la primera clase del día empieza a validar el QR en un local " +
            "sin recepción.",
        help: "Los locales sin mostrador abren según su grilla de clases: el QR valida desde estos " +
            "minutos antes de que empiece la primera clase del día hasta que termina la última, y " +
            "fuera de esa ventana rechaza con motivo «local cerrado». Un día sin clases programadas, " +
            "el local no abre. Con 30, si la primera clase es a las 8:00 se puede entrar desde las " +
            "7:30; con 60, desde las 7:00; con 0, recién cuando empieza la clase. No cambia nada en " +
            "los locales con recepción: ahí decide quien está en el mostrador.",
        unit: "minutos",
    }),
    "access.exceptionAlertCount": def({
        scope: "global",
        schema: z.number().int().min(1),
        default: 3,
        rule: "RN-ACC-14",
        pv: "PV-42",
        editors: "general",
        label: "Excepciones repetidas que avisan a Administración",
        description: "Excepciones de ingreso por el mismo motivo a partir de las cuales Dueño y " +
            "Administración reciben un aviso.",
        help: "Una excepción de ingreso deja pasar por esa única vez a un socio rechazado (por deuda, " +
            "apto vencido, etc.), con firma de un encargado y motivo obligatorio. Para que no se " +
            "vuelva costumbre, el sistema cuenta las excepciones de cada socio por motivo de " +
            "bloqueo: cuando llegan a este número, Dueño y Administración reciben un aviso, que se " +
            "repite con cada excepción siguiente. Con 3, tres autorizaciones por «cuota vencida» " +
            "disparan el aviso; tres por motivos distintos, no. Con 1 se avisa desde la primera. La " +
            "excepción en sí no cambia: sigue sin saldar la deuda ni extender el apto.",
        unit: "excepciones",
    }),
    // ── Molinete ZKTeco (docs/decisiones/molinete-zkteco.md) ──────────────────
    "access.zk.openSeconds": def({
        scope: "global",
        schema: z.number().int().min(1).max(60),
        default: 5,
        rule: "RN-ACC-07",
        editors: "general",
        label: "Segundos que queda liberado el molinete al abrir desde el sistema",
        description: "Cuánto dura la apertura cuando recepción aprieta «Abrir molinete».",
        help: "Al apretar «Abrir molinete» en recepción, el terminal acciona el relé durante estos " +
            "segundos. Con 5 hay tiempo de sobra para pasar; si el molinete se traba antes de que " +
            "la persona gire, subilo. No cambia nada para quien entra con su huella: esa apertura " +
            "la maneja el propio terminal.",
        unit: "segundos",
    }),
    "access.zk.commandTimeoutSec": def({
        scope: "global",
        schema: z.number().int().min(15).max(3600),
        default: 120,
        rule: "RN-ACC-02",
        editors: "general",
        label: "Segundos para dar por perdido un comando al terminal",
        description: "Si el terminal no confirma un comando en este tiempo, se le vuelve a mandar.",
        help: "Cada alta, bloqueo o carga de huella que el sistema le manda al terminal del molinete " +
            "espera su confirmación. Si no llega en estos segundos (el equipo se reinició, se cortó " +
            "la red), se le vuelve a mandar en la próxima consulta del equipo, hasta agotar los " +
            "intentos. Con 120, un corte de dos minutos no pierde nada.",
        unit: "segundos",
    }),
    "access.zk.maxAttempts": def({
        scope: "global",
        schema: z.number().int().min(1).max(50),
        default: 5,
        rule: "RN-ACC-02",
        editors: "general",
        label: "Intentos por comando al terminal antes de marcarlo con error",
        description: "Cuántas veces se reintenta un comando al terminal antes de mostrarlo como error.",
        help: "Agotados los intentos, el comando queda con error en Dispositivos de acceso → ZKTeco, " +
            "donde se puede reintentar a mano. La reconciliación de cada 15 minutos vuelve a " +
            "calcular lo que falte, así que un error no deja a un socio mal habilitado para siempre.",
        unit: "intentos",
    }),
    // ── Estructura y horarios (AF §1.4) ───────────────────────────────────────
    "branch.schedule": def({
        scope: "branch",
        schema: scheduleTable,
        // Default = la fila más amplia de la tabla del AF §1.4 (Martínez). Cada sede
        // guarda el suyo; el seed carga las 3 sedes con sus horarios reales.
        default: {
            weekdays: { open: "07:00", close: "22:00" },
            saturday: { open: "08:00", close: "17:00" },
            sunday: { open: "09:00", close: "13:00" },
            holidays: { open: "09:00", close: "13:00" },
        },
        rule: "AF §1.4 (horarios por sede)",
        editors: "general",
        label: "Horarios de la recepción de la sede",
        description: "Apertura y cierre de la recepción según el tipo de día; un día sin horario (null) es un día cerrado.",
        help: "Es la tabla de horarios de la recepción de esta sede, y de acá salen tres cosas: el rango del turno " +
            "único de caja de sábados, domingos y feriados; si la sede abre o no un día dado, que es lo que usan las " +
            "alertas de negocio para comparar la asistencia y la facturación sólo contra días en que la sede " +
            "realmente operó; y el texto de la respuesta automática que recibe un socio que escribe fuera de " +
            "horario, donde se listan estos horarios. Se carga por tipo de día: weekdays (lunes a viernes), saturday " +
            "(sábado), sunday (domingo) y holidays (feriados), cada uno con open y close en formato de 24 horas, " +
            'por ejemplo { "open": "08:00", "close": "15:00" }. Si la sede no abre ese día se pone null en lugar ' +
            "del horario: así se cargan La Lucila y Olivos los domingos. Es por sede, y tiene que coincidir con lo " +
            "que la recepción hace de verdad: un domingo marcado como abierto sin nadie atendiendo aparece en las " +
            "alertas como un día de asistencia cero.",
    }),
    "branch.cashShiftCut": def({
        scope: "branch",
        schema: cashShiftCut,
        default: {
            morning: { from: "07:00", to: "14:30" },
            afternoon: { from: "14:30", to: "22:00" },
            singleShiftWeekdays: [0, 6], // domingo y sábado
        },
        rule: "RN-CAJ-06",
        pv: "PV-59",
        editors: "general",
        label: "Corte de los turnos de caja",
        description: "A qué hora termina el turno mañana y empieza el tarde, y qué días de la semana tienen un solo turno.",
        help: "Cada día de operación tiene dos turnos de caja, mañana y tarde, y esta tabla define en esta sede a qué " +
            "hora corta uno y arranca el otro: al abrir una caja, el sistema mira la hora y le asigna el turno que " +
            "corresponde. morning y afternoon llevan from y to en formato de 24 horas; con el valor de fábrica, una " +
            "caja abierta a las 14:20 es del turno mañana y una abierta a las 14:35 es del turno tarde. " +
            "singleShiftWeekdays es la lista de días de la semana que operan con un solo turno, numerados de 0 " +
            "(domingo) a 6 (sábado): [0, 6] son domingos y sábados; los feriados van siempre con turno único, sin " +
            "cargarlos acá. Si en una sede el turno mañana termina a las 15:00, cambiá el to de morning y el from " +
            "de afternoon a la misma hora, sin dejar huecos entre uno y otro.",
    }),
    holidays: def({
        scope: "global",
        schema: z.record(z.string(), z.array(z.string())),
        default: {},
        rule: "RN-PRO-05 / RN-CLA-10",
        editors: "holiday_calendar",
        label: "Calendario de feriados del club",
        description: "Fechas feriadas por año que usan la grilla de clases, el pago de la hora y los días " +
            "hábiles de caja.",
        help: "Es la lista de días que el sistema trata como feriados, cargada por año con sus fechas: " +
            '{"2026": ["2026-01-01", "2026-05-01"]}. Un día que figura acá cambia varias cosas: al ' +
            "publicar la grilla del mes no se generan las clases de ese día, salvo las que el " +
            "coordinador marque como dictadas; la hora del profesor que igual trabaja se paga con el " +
            "recargo de feriado del tarifario (si cae en domingo, se aplica el mayor de los dos " +
            "recargos, no los dos juntos); la caja de ese día opera con un solo turno; y no cuenta " +
            "como día hábil para observar una transferencia ni para esperar la acreditación de la " +
            "terminal. Sin nada cargado, ningún día es feriado: el 25 de mayo se pagaría como día " +
            "común y la grilla lo llenaría de clases. Lo cargan el Dueño, Administración, " +
            "Coordinación y los encargados de sede. Lo más cómodo es cargarlo mes a mes desde el " +
            "Editor de grilla mensual (tarjeta Feriados del mes), o año por año desde Personal → " +
            "Tarifario y multiplicadores, pestaña Feriados; acá se ve el calendario completo. " +
            "Conviene tener cargado el año siguiente antes de publicar su primera grilla. " +
            "Las alertas de negocio usan un calendario aparte.",
    }),
    // ── Caja (AF §8) ──────────────────────────────────────────────────────────
    "cash.blindMode": def({
        scope: "branch",
        schema: z.boolean(),
        default: true,
        rule: "RN-CAJ-09",
        pv: "PV-58",
        editors: "general",
        label: "Cierre de caja a ciegas",
        description: "Al cerrar el turno, quien atiende cuenta y declara el efectivo sin ver cuánto debería " +
            "haber.",
        help: "Activado, la recepcionista no ve durante el turno cuánto efectivo debería haber en la " +
            "caja: sólo su lista de cobros. Al cerrar, cuenta la plata, la declara, y recién después " +
            "el sistema muestra lo esperado, lo declarado y la diferencia. Dueños y Administración " +
            "ven el esperado siempre. Desactivado, la recepcionista ve el efectivo esperado en todo " +
            "momento y puede ir chequeando la caja contra el sistema, con lo que una diferencia se " +
            "puede «acomodar» antes del cierre. Se puede fijar distinto por sede, y el cambio rige " +
            "para los turnos que se abran después, no para el que está abierto.",
    }),
    "cash.discountRoles": def({
        scope: "global",
        schema: z.array(z.string()).min(1),
        // RN-CAJ-20 (rel. 8.5): «los descuentos los autorizan sólo los dueños».
        default: ["owner"],
        rule: "RN-CAJ-20",
        pv: "PV-64",
        editors: "owner_only",
        label: "Roles que pueden autorizar descuentos",
        description: "Quiénes pueden autorizar un descuento sobre el precio de lista al cobrar. De fábrica, " +
            "sólo el Dueño.",
        help: "Un descuento es siempre una línea explícita sobre el precio de lista, con motivo " +
            "obligatorio y con la persona que lo autorizó: no existe el «precio manual». Este " +
            "parámetro dice qué roles pueden ser esa persona; si quien autoriza no tiene uno de " +
            "estos roles, el cobro se rechaza. Se carga como lista de códigos de rol entre corchetes " +
            "y comillas: owner (Dueño), admin (Administración), intersede_coordinator (Coordinador " +
            "intersedes), branch_manager (Encargado de sede), receptionist (Recepcionista). Por " +
            'ejemplo, ["owner", "admin"] habilita a los dueños y a la administradora. Ampliarlo hace ' +
            "más ágil el mostrador pero reparte una decisión que hoy sólo toman los dueños; cada " +
            "descuento queda registrado igual, con quién lo autorizó y por qué.",
    }),
    "cash.envelopeCap": def({
        scope: "global",
        schema: moneyInt.min(0),
        default: 50_000_000, // $500.000,00
        rule: "RN-CAJ-13",
        pv: "PV-61",
        editors: "financial",
        label: "Tope de efectivo para armar un sobre",
        description: "Efectivo acumulado en la caja de un turno a partir del cual conviene armar un sobre sin " +
            "esperar al cierre.",
        help: "Es la referencia de cuánto efectivo puede juntarse en la caja de un turno. Cuando lo " +
            "acumulado la supera, corresponde preparar un sobre intermedio desde Caja del turno " +
            "(botón «Preparar sobre»): el efectivo pasa a un sobre numerado de la sede y deja de " +
            "estar en el cajón. Al cierre, todo lo que no queda de remanente va a un sobre de todos " +
            "modos; este tope existe para que en un día fuerte la plata no espere hasta la noche. Se " +
            "carga en centavos, sin puntos ni coma: $500.000 se escribe 50000000. Con un tope bajo " +
            "se arman más sobres por día (y más retiros que registrar); con uno alto hay más " +
            "efectivo en el cajón durante más horas.",
        unit: "$",
    }),
    "cash.pettyExpenseCap": def({
        scope: "global",
        schema: moneyInt.min(0),
        default: 5_000_000, // $50.000,00
        rule: "RN-CAJ-15",
        pv: "PV-67",
        editors: "financial",
        label: "Tope de gasto menor pagado desde la caja",
        description: "Máximo de un gasto pagado con efectivo de la caja que una recepcionista puede registrar " +
            "sin un superior.",
        help: "Los gastos chicos del día (agua, una lamparita, un flete) se pagan con efectivo de la " +
            "caja y se registran en el momento contra el turno, con la foto del ticket. Hasta este " +
            "importe los registra quien atiende; por encima, el sistema sólo lo acepta si lo " +
            "registra un Encargado de sede o alguien superior, con el motivo de la aprobación. El " +
            "gasto descuenta del efectivo esperado del turno y pasa al circuito normal de egresos, " +
            "donde nace pendiente de aprobación. Se carga en centavos, sin puntos ni coma: $50.000 " +
            "se escribe 5000000. Subirlo da más autonomía en recepción; bajarlo obliga a que más " +
            "gastos pasen por la encargada antes de salir de la caja.",
        unit: "$",
    }),
    "cash.envelopeAlertHours": def({
        scope: "global",
        schema: z.number().int().min(1),
        default: 24,
        rule: "RN-CAJ-14",
        editors: "general",
        label: "Horas de sobre sin retirar antes del aviso",
        description: "Cuántas horas puede quedar un sobre de efectivo sin retirar en la sede antes de " +
            "avisarles a los dueños.",
        help: "Cada sobre que se arma queda «en sede» hasta que un dueño lo retira con doble " +
            "confirmación. Si pasa más de esta cantidad de horas sin retirarse, todos los dueños " +
            "reciben un aviso (en el celular, por mail y en el portal), una sola vez por sobre. El " +
            "control se hace cada hora. Con 24, el sobre de un cierre del lunes a la noche avisa el " +
            "martes a la noche; con 48, recién el miércoles. Bajarlo sirve si quieren enterarse el " +
            "mismo día; subirlo evita avisos en sedes donde el retiro es cada dos o tres días.",
        unit: "horas",
    }),
    "cash.posAutoSettleTime": def({
        scope: "global",
        schema: timeHHmm,
        default: "23:30",
        rule: "RN-CAJ-17",
        editors: "general",
        label: "Hora del cierre de lote automático de los posnet",
        description: "A partir de esta hora, el sistema le pide el cierre de lote a cada posnet que tenga " +
            "cobros sin lote.",
        help: "Payway cobra un cargo cuando una terminal pasa el día sin cerrar el lote. Todos los " +
            "días, desde esta hora y hasta la medianoche, el sistema le pide el cierre a cada posnet " +
            "activo que tenga cobros todavía sin lote, trae los cupones y los cruza contra los " +
            "cobros registrados: lo que no coincide queda en Diferencias. Si la terminal rechaza el " +
            "cierre, se avisa al encargado de la sede. Conviene una hora posterior al último cobro " +
            "del día; con 23:30, los cobros que entren después quedan para el lote siguiente.",
        unit: "hora",
    }),
    "cash.posTerminalMode": def({
        scope: "branch",
        schema: z.enum(POS_TERMINAL_MODES),
        // Pedido del club (13-oct-2026): por ahora se cobra con el posnet físico y se carga el cupón.
        default: "manual",
        rule: "RN-CAJ-16 / RN-CAJ-18",
        editors: "financial",
        label: "Cómo se cobra con el posnet",
        description: "manual: se pasa la tarjeta en el posnet y recepción carga el cupón. integrated: el " +
            "sistema le manda el importe a la terminal.",
        help: "Con manual, el socio paga en el posnet como siempre y recepción carga en Cobrar en " +
            "mostrador la terminal, el número de cupón y, si lo tiene a mano, el número de lote. El " +
            "importe sale igual del sistema (lista de precios, promo y beneficio del socio), la " +
            "factura sale sola y el cupón queda pendiente de cruzarse contra el cierre de lote. Un " +
            "mismo cupón no respalda dos cobros. Los dueños reciben un resumen diario con los cobros " +
            "cargados a mano, en vez de un aviso por cada uno. Con integrated, el sistema le manda el " +
            "importe a la terminal y el resultado vuelve solo; la carga del cupón a mano queda sólo " +
            "para cuando la terminal no responde, la hace un encargado de sede o superior con motivo " +
            "y avisa a los dueños en el momento. Se escribe tal cual, en minúsculas: manual o " +
            "integrated. Se puede fijar distinto por sede. Para pasar a integrated tiene que estar " +
            "configurada la integración del posnet en Ajustes → Integraciones.",
    }),
    "cash.defaultRemainder": def({
        scope: "branch",
        schema: moneyInt.min(0),
        default: 0,
        rule: "RN-CAJ-10",
        pv: "PV-60",
        editors: "financial",
        label: "Fondo de cambio al cerrar la caja",
        description: "Efectivo de referencia que queda en la caja al cerrar el turno, para el siguiente. 0 = " +
            "sin fondo fijo.",
        help: "Al cerrar, quien atiende cuenta el efectivo, declara cuánto deja en la caja como " +
            "remanente (el cambio para el turno siguiente) y lo demás va a un sobre. El turno " +
            "siguiente abre esperando exactamente ese remanente: si el efectivo contado no coincide, " +
            "queda una diferencia de apertura contra los dos usuarios. Este valor es el fondo de " +
            "cambio acordado para la sede, la referencia de cuánto se espera que quede; con 0 el " +
            "remanente es variable y lo decide cada cierre. Se carga en centavos, sin puntos ni " +
            "coma: $30.000 se escribe 3000000, y se puede fijar distinto por sede. Hoy la pantalla " +
            "de cierre no lo completa sola: es una referencia para quien cierra y para quien revisa " +
            "los cierres.",
        unit: "$",
    }),
    "transfer.observeBusinessDays": def({
        scope: "global",
        schema: z.number().int().min(1),
        default: 3,
        rule: "RN-CAJ-19.5",
        pv: "PV-63",
        editors: "general",
        label: "Días hábiles para observar una transferencia",
        description: "Días hábiles que puede tardar una transferencia declarada en verse en el banco antes de " +
            "quedar observada.",
        help: "Un cobro por transferencia se registra en el momento y habilita al socio enseguida; el " +
            "banco lo confirma después. Cada mañana el sistema revisa los movimientos de las cuentas " +
            "del club: si pasaron estos días hábiles (no cuentan fines de semana ni los feriados del " +
            "calendario) y la transferencia no apareció, el cobro queda «observado», Administración " +
            "recibe una alerta y el socio un aviso pidiéndole el comprobante. Si el banco no está " +
            "informando, no se observa nada: no se acusa a nadie por un dato que no se pudo ver. Con " +
            "3, una transferencia declarada el lunes se observa el jueves; con 5, el lunes " +
            "siguiente. Tiene que ser menor que los días para revertir.",
        unit: "días hábiles",
    }),
    "transfer.revertBusinessDays": def({
        scope: "global",
        schema: z.number().int().min(1),
        default: 7,
        rule: "RN-CAJ-19.5",
        pv: "PV-63",
        editors: "general",
        label: "Días hábiles para revertir una transferencia",
        description: "Días hábiles sin acreditarse tras los que el cobro se anula y la cuota vuelve a figurar " +
            "impaga.",
        help: "Se cuenta desde que se declaró la transferencia, en días hábiles. Si el cobro ya quedó " +
            "observado y al llegar a este plazo la plata sigue sin aparecer en el banco, el sistema " +
            "da de baja la habilitación que ese cobro otorgó: la cuota vuelve a estar impaga, el " +
            "socio recibe el aviso y desde ahí rigen las reglas de deuda de cualquier cuota vencida. " +
            "Si Administración concilia la transferencia antes, no pasa nada. Con 7, una " +
            "transferencia declarada un lunes que nunca llegó se revierte el miércoles de la semana " +
            "siguiente. Un plazo corto corta antes el abuso; uno largo tolera demoras bancarias " +
            "reales. Tiene que ser mayor que los días para observar.",
        unit: "días hábiles",
    }),
    // ── Caja chica (AF §9) ────────────────────────────────────────────────────
    "pettyCash.capDefault": def({
        scope: "global",
        schema: moneyInt.min(0),
        default: 10_000_000, // $100.000,00
        rule: "RN-CON-14",
        pv: "PV-72",
        editors: "financial",
        label: "Tope inicial de la caja chica",
        description: "Importe con el que nace el fondo de caja chica de una sede cuando se crea sin indicarle " +
            "un tope propio.",
        help: "La caja chica es un fondo fijo por sede para gastos menores, separado de la caja de " +
            "recepción. Nace desactivada: se crea con un responsable y un tope, y se activa con " +
            "motivo el día que decidan usarla. Este parámetro es el tope que toma un fondo nuevo si " +
            "al crearlo no se escribe otro; el tope de cada fondo ya creado se edita en su propia " +
            "pantalla y no cambia por tocar este valor. Se carga en centavos, sin puntos ni coma: " +
            "$100.000 se escribe 10000000. Tiene sentido cambiarlo antes de crear los fondos de las " +
            "sedes, para no cargar el mismo número tres veces.",
        unit: "$",
    }),
    "expense.autoApproveThreshold": def({
        scope: "global",
        schema: moneyInt.min(0),
        default: 0,
        rule: "RN-CON-12",
        pv: "PV-71",
        editors: "financial",
        label: "Monto de egreso que se aprueba solo",
        description: "Egresos de hasta este importe nacen aprobados sin pasar por dueños o Administración. 0 " +
            "= nada se aprueba solo.",
        help: "Todo egreso nace pendiente y lo aprueban los dueños o Administración. Si acá se carga " +
            "un importe, los egresos de hasta ese monto nacen aprobados, con la nota de que fue " +
            "automático, siempre que traigan comprobante o la marca «sin comprobante» con motivo; " +
            "sin ninguna de las dos, quedan pendientes aunque el monto sea menor. Si el egreso es " +
            "una compra de mercadería del kiosko, el stock entra en el acto. Con 0, nada se aprueba " +
            "solo. Se carga en centavos, sin puntos ni coma: $20.000 se escribe 2000000. Subirlo " +
            "saca trámite chico de la bandeja de aprobación; dejarlo en 0 mantiene la mirada de un " +
            "superior sobre cada peso que sale, que es el motivo por el que existe el sistema.",
        unit: "$",
    }),
    // ── Débito automático (AF §4.8) ───────────────────────────────────────────
    "debit.retrySchedule": def({
        scope: "global",
        schema: z.array(z.number().int().min(1)),
        default: [24, 72],
        rule: "RN-PLA-25",
        pv: "PV-19",
        editors: "general",
        label: "Reintentos del débito rechazado",
        description: "Cuántas veces y con cuántas horas de espera se vuelve a intentar un débito que la " +
            "tarjeta rechazó.",
        help: "Cuando el débito del día del vencimiento es rechazado, el sistema vuelve a intentarlo " +
            "solo. Se carga como lista de horas entre corchetes: cada número es cuántas horas " +
            "después del rechazo anterior se hace el siguiente intento, y la cantidad de números es " +
            "la cantidad de reintentos. Con [24, 72], el primer reintento es un día después del " +
            "rechazo y el segundo, tres días después del primero; con [24] hay un solo reintento; " +
            "con [] no hay ninguno. Mientras queden reintentos pendientes la cuota figura «en " +
            "gestión de cobro», el socio sigue entrando y no recibe aviso de deuda; recién cuando se " +
            "agotan la cuota pasa a deuda, se le avisa al socio y empieza a contar el bloqueo. Más " +
            "reintentos dan más chances de cobrar sin molestar al socio, pero estiran los días en " +
            "que una cuota rechazada no bloquea.",
        unit: "horas",
    }),
    "debit.cashOnlyOnReject": def({
        scope: "global",
        schema: z.boolean(),
        default: false,
        rule: "RN-PLA-25 / RN-CAJ-21",
        pv: "PV-20",
        editors: "general",
        label: "Cuota con débito rechazado: sólo en efectivo",
        description: "Si una cuota cuyo débito fue rechazado se cobra sólo en efectivo o con cualquier medio " +
            "en recepción.",
        help: "El consentimiento en papel dice que una cuota con débito rechazado se paga sólo en " +
            "efectivo. Activado, cuando se agotan los reintentos la cuota queda marcada con esa " +
            "restricción: en Cobrar en mostrador no se puede elegir otro medio que efectivo para esa " +
            "cuota, y la cuenta corriente del socio muestra la marca. Desactivado (como viene de " +
            "fábrica), esa cuota se cobra con cualquier medio presencial y queda registrado cuál. El " +
            "cambio rige enseguida en el mostrador, también para las cuotas rechazadas antes. " +
            "Activarlo es fiel al papel; dejarlo apagado evita rechazar un pago con tarjeta en el " +
            "mostrador por una regla que nadie recuerda.",
    }),
    "debit.permanenceMonths": def({
        scope: "global",
        schema: z.number().int().min(0),
        default: 4,
        rule: "RN-PLA-24",
        editors: "general",
        label: "Permanencia mínima en el débito automático",
        description: "Meses desde la adhesión durante los cuales no se acepta ningún pedido de baja del " +
            "débito.",
        help: "Al adherirse al débito, el socio obtiene el precio preferencial y asume una permanencia " +
            "mínima. Durante estos meses, contados desde el día de la adhesión, el sistema rechaza " +
            "cualquier pedido de baja del débito, venga de la app o de recepción, sin excepción por " +
            "viajes ni enfermedad; el mensaje le dice al socio desde qué fecha puede pedirla. " +
            "Actualizar la tarjeta no reinicia el plazo. Con 4, quien adhiere el 10 de marzo puede " +
            "pedir la baja a partir del 10 de julio; con 0 no hay permanencia. Sólo afecta a las " +
            "adhesiones nuevas: las existentes conservan la fecha que se calculó al adherirse.",
        unit: "meses",
    }),
    "debit.maxRejectionsBeforeSuspend": def({
        scope: "global",
        schema: z.number().int().min(1),
        default: 3,
        rule: "RN-PLA-25",
        editors: "general",
        label: "Rechazos de débito antes de suspender la adhesión",
        description: "Cantidad de cuotas con débito rechazado y sin pagar a partir de la cual el débito del " +
            "socio se suspende.",
        help: "Cada vez que un débito agota sus reintentos, el sistema cuenta cuántas cuotas de ese " +
            "socio tienen débito rechazado y siguen impagas. Si llega a este número, la adhesión " +
            "pasa a «suspendida»: el sistema deja de presentar sus cuotas a la tarjeta y el socio " +
            "paga en recepción hasta que actualice la tarjeta, lo que vuelve a activar el débito. " +
            "Las cuotas rechazadas que se pagan dejan de contar. Con 3, el tercer mes seguido de " +
            "rechazo sin pagar suspende; con 1, el primer rechazo agotado ya suspende. Un número " +
            "bajo evita presentar una tarjeta muerta mes a mes; uno alto tolera rechazos ocasionales " +
            "por límite o vencimiento.",
        unit: "cuotas",
    }),
    "debit.cancelWindowDays": def({
        scope: "global",
        schema: z.number().int().min(1),
        default: 5,
        rule: "RN-PLA-26",
        pv: "PV-18",
        editors: "general",
        label: "Ventana mensual para pedir la baja del débito",
        description: "Primeros días del mes del próximo vencimiento en que pedir la baja del débito frena esa " +
            "cuota.",
        help: "La baja del débito se pide con motivo y con fecha futura. Si el pedido se hace dentro " +
            "de los primeros días del mes calendario en que cae el próximo vencimiento (tantos días " +
            "como diga este valor), esa cuota ya no se debita y la baja rige desde ese vencimiento. " +
            "Pasada la ventana, la cuota en curso se debita igual y la baja rige desde el ciclo " +
            "siguiente; la pantalla se lo dice al socio antes de confirmar. Con 5, un socio que " +
            "vence el 20 de cada mes tiene hasta el 5 de ese mes para frenar esa cuota; si pide el " +
            "8, se le debita el 20 y la baja rige desde el mes siguiente. Sólo se acepta si ya " +
            "cumplió la permanencia mínima. La baja del débito no es la baja del socio: sigue como " +
            "mensual sin precio preferencial.",
        unit: "días",
    }),
    "debit.prismanet.establishments": def({
        scope: "global",
        schema: z.object({
            visa_credit: z
                .string()
                .regex(/^\d{8}$/)
                .nullable(),
            visa_debit: z
                .string()
                .regex(/^\d{8}$/)
                .nullable(),
            master_credit: z
                .string()
                .regex(/^\d{8}$/)
                .nullable(),
            master_debit: z
                .string()
                .regex(/^\d{8}$/)
                .nullable(),
        }),
        default: {
            visa_credit: "73559999",
            visa_debit: null,
            master_credit: "73560005",
            master_debit: null,
        },
        rule: "RN-PLA-25",
        editors: "financial",
        label: "Establecimientos del débito por archivo (PrismaNet)",
        description: "Número de establecimiento de Prisma de cada tipo de tarjeta con el que se arma el " +
            "archivo de presentación del débito automático clásico.",
        help: "El débito automático clásico se presenta con un archivo por tipo de tarjeta (Visa " +
            "crédito, Visa débito, Mastercard crédito, Mastercard débito), y cada uno lleva en el " +
            "encabezado el número de establecimiento de 8 dígitos que Prisma le asignó al club " +
            "para ese tipo (es también el usuario de PrismaNet con el que se sube). Se cargan como " +
            "lista: visa_credit, visa_debit, master_credit y master_debit; vacío (null) significa " +
            "que el club no tiene establecimiento para ese tipo, y entonces el sistema no acepta " +
            "tarjetas de ese tipo en el débito por archivo y avisa al cargarlas. Si Prisma cambia " +
            "un número, se cambia acá y rige desde la próxima presentación.",
    }),
    "debit.prismanet.debitBins": def({
        scope: "global",
        schema: z.array(z.string().regex(/^\d{6,8}$/)),
        default: [],
        rule: "RN-PLA-25",
        editors: "financial",
        label: "BIN de tarjetas de débito (débito por archivo)",
        description: "Primeros 6 a 8 dígitos de las tarjetas que son de débito, para mandarlas al archivo " +
            "de débito y no al de crédito.",
        help: "Visa y Mastercard usan el mismo rango de números para crédito y débito: lo que " +
            "distingue una tarjeta de débito son sus primeros dígitos (el BIN), según la tabla que " +
            "entrega Payway. Al cargar una tarjeta para el débito por archivo, si sus primeros " +
            "dígitos están en esta lista el sistema la manda al archivo de débito aunque se haya " +
            "elegido crédito, y lo avisa. Con la lista vacía se usa el tipo que se elige en la " +
            'pantalla. Se carga como lista entre corchetes, por ejemplo ["451761", "400276"].',
    }),
    "debit.prismanet.lookaheadDays": def({
        scope: "global",
        schema: z.number().int().min(0).max(31),
        default: 7,
        rule: "RN-PLA-25",
        editors: "general",
        label: "Días hacia adelante de la presentación por archivo",
        description: "Hasta cuántos días después de hoy toma, por default, las cuotas que vencen al armar " +
            "la presentación del débito por archivo.",
        help: "Al abrir «Presentar débitos» el rango propuesto va desde el día siguiente a la última " +
            "presentación hasta hoy más este número de días. Cada socio se debita en la fecha de " +
            "vencimiento de SU cuota, que viaja en el archivo: presentar con días de anticipación " +
            "no adelanta el débito. Con 7, la presentación de un lunes toma lo que vence hasta el " +
            "lunes siguiente. El rango se puede cambiar a mano en la pantalla.",
        unit: "días",
    }),
    // ── Planes y socios (AF §3/§4) ────────────────────────────────────────────
    "member.inactiveAfterDays": def({
        scope: "global",
        schema: z.number().int().min(1),
        default: 60,
        rule: "RN-SOC-28 / RN-PLA-12",
        pv: "PV-07",
        editors: "general",
        label: "Días sin renovar para pasar a inactivo",
        description: "Días desde el último vencimiento sin pagar tras los que el socio pasa a inactivo y " +
            "vuelve a deber matrícula.",
        help: "Todas las madrugadas el sistema recalcula el estado de cada socio: al día es «activo», " +
            "con deuda más allá de la tolerancia es «moroso», y con este número de días o más desde " +
            "su último vencimiento sin renovar pasa a «inactivo». Cuando un inactivo vuelve, la " +
            "matrícula se agrega sola a su primer cobro (salvo que tome un plan largo o débito " +
            "automático, que la bonifican); si vuelve antes de cumplir estos días, no la paga. Con " +
            "60, quien dejó impaga la cuota que vencía el 1 de marzo pasa a inactivo el 30 de abril " +
            "y desde ahí su reingreso lleva matrícula; con 90 tiene un mes más de margen. Bajarlo " +
            "cobra matrícula antes; subirlo mantiene más gente en la lista de morosos.",
        unit: "días",
    }),
    "family.discountTarget": def({
        scope: "global",
        schema: z.enum(FAMILY_DISCOUNT_TARGETS),
        // Flyers de octubre 2026: «DESCUENTO GRUPO FAMILIAR sobre el plan de menor valor».
        default: "lowest_plan",
        rule: "RN-PLA-05 / RN-SOC-19",
        pv: "PV-09",
        editors: "financial",
        label: "A qué cuota se aplica el descuento del grupo familiar",
        description: "Si el porcentaje del grupo familiar descuenta sólo la cuota del plan de menor valor " +
            "del grupo o la cuota de cada integrante.",
        help: "El porcentaje del grupo familiar se carga en la lista de precios; este parámetro dice " +
            "sobre qué cuota se aplica. Con lowest_plan (lo que comunican los flyers) se descuenta " +
            "sólo la cuota del integrante cuyo plan vale menos en la lista vigente; si dos " +
            "integrantes empatan, el de menor número de socio. Con each_member se descuenta la " +
            "cuota de cada integrante, como se hacía antes. Ejemplo con 10 %: un grupo con pase " +
            "libre a $85.000 y Musculación a $74.000 paga $85.000 + $66.600 con lowest_plan y " +
            "$76.500 + $66.600 con each_member. Se escribe exactamente lowest_plan o each_member; " +
            "el cambio rige desde el próximo cobro.",
    }),
    "referral.creditValidityDays": def({
        scope: "global",
        schema: z.number().int().min(1).max(365),
        default: 90,
        rule: "RN-PLA-20",
        editors: "financial",
        label: "Días que dura el crédito por referir",
        description: "Cuántos días tiene el socio que refirió para usar su descuento en una cuota antes de que venza.",
        help: "Cuando alguien se asocia con una promo de referidos, el socio que lo refirió recibe un " +
            "descuento para su próxima cuota cobrada en el mostrador. Ese crédito se usa una sola " +
            "vez y vence a esta cantidad de días desde el cobro del referido. Con 90, si el " +
            "referido se asocia el 1 de octubre, el que refirió tiene hasta fines de diciembre para " +
            "usarlo. El cambio rige para los créditos que nazcan desde ese momento.",
        unit: "días",
    }),
    "charges.generateAheadDays": def({
        scope: "global",
        schema: z.number().int().min(0).max(31),
        default: 5,
        rule: "RN-PLA-06 / RN-PLA-11",
        editors: "general",
        label: "Anticipación con que se emite la cuota",
        description: "Cuántos días antes del vencimiento aparece la cuota del próximo período en la cuenta " +
            "corriente del socio.",
        help: "Cada madrugada el sistema emite las cuotas mensuales que vencen dentro de esta cantidad " +
            "de días, al precio de la lista que rige en la fecha de vencimiento (no en la de emisión " +
            "ni en la de pago). Con 5, la cuota que vence el 20 se emite el 15 y desde entonces " +
            "figura en la cuenta corriente del socio; con 0 se emite el mismo día del vencimiento. " +
            "Ojo con el aviso «se te vence la cuota»: sólo puede salir si la cuota ya está emitida, " +
            "así que este valor tiene que ser mayor o igual que los días de anticipación de ese " +
            "aviso (3 de fábrica). Los planes largos no dependen de esto: sus cuotas se emiten todas " +
            "al venderlos.",
        unit: "días",
    }),
    "charges.renewalWindowDays": def({
        scope: "global",
        schema: z.number().int().min(0).max(365),
        // El primer aviso de vencimiento sale a los 30 días (RN-SOC-12): desde ahí se puede renovar.
        default: 30,
        rule: "RN-PLA-03 / RN-PLA-13 / RN-SOC-12",
        editors: "general",
        label: "Días antes del vencimiento en que se puede renovar desde la app",
        description: "Desde cuántos días antes de que venza un plan de duración fija el socio puede " +
            "comprar la renovación desde la app.",
        help: "Un plan trimestral, semestral, anual, quincena o semana se renueva comprando el plan " +
            "otra vez: el nuevo arranca el día en que vence el actual, sin superponerse. Este " +
            "número dice con cuánta anticipación la app deja hacerlo. Con 30, un anual que vence " +
            "el 15/10 se puede renovar desde el 15/9; antes, la app le dice desde qué fecha puede. " +
            "El precio de la renovación es el de la lista que rige el día en que arranca el plan " +
            "nuevo. El pase libre mensual no se renueva así: se paga la cuota de cada mes.",
        unit: "días",
    }),
    // ── Socios (AF §3) — bloque del módulo `socios` ───────────────────────────
    "member.minAgeYears": def({
        scope: "global",
        schema: z.number().int().min(0),
        default: 13,
        rule: "RN-SOC-15",
        editors: "general",
        label: "Edad mínima para asociarse",
        description: "Años cumplidos que hacen falta para dar de alta un socio; todo menor de 18 lleva además " +
            "adulto responsable.",
        help: "El alta se rechaza si la persona tiene menos años que este valor, tanto en recepción " +
            "como desde la app, y también al corregir la fecha de nacimiento de una ficha. Aparte de " +
            "este piso, cualquier menor de 18 necesita un adulto responsable vinculado, que firma la " +
            "autorización, el reglamento y la cesión de imagen; eso no se configura. Con 13, alguien " +
            "de 12 no puede asociarse ni con autorización de los padres; con 16, tampoco uno de 15. " +
            "Hoy el club acepta desde los 13: subirlo tiene sentido si deciden no recibir " +
            "adolescentes.",
        unit: "años",
    }),
    "member.incompleteReminderDays": def({
        scope: "global",
        schema: z.number().int().min(1),
        default: 7,
        rule: "RN-SOC-07",
        editors: "general",
        label: "Días entre recordatorios de ficha incompleta",
        description: "Cada cuántos días la app le vuelve a pedir al socio los datos que le faltan a su ficha.",
        help: "Cuando a una ficha le faltan datos (dirección, mail, contacto de emergencia, apto " +
            "médico o huella), todas las mañanas el sistema revisa a los socios activos y morosos y " +
            "les manda un aviso a la app pidiendo lo que falta; a un menor le llega al adulto " +
            "responsable. Este valor es el descanso entre un aviso y el siguiente para la misma " +
            "persona. Con 7, quien recibió el pedido el lunes no vuelve a recibirlo hasta el lunes " +
            "siguiente; con 3, lo recibe dos veces por semana. Apenas completa la ficha, los avisos " +
            "paran solos. Va de la mano del tope de recordatorios.",
        unit: "días",
    }),
    "member.incompleteReminderMax": def({
        scope: "global",
        schema: z.number().int().min(1),
        default: 6,
        rule: "RN-SOC-07",
        editors: "general",
        label: "Tope de recordatorios de ficha incompleta",
        description: "Cuántos avisos como máximo recibe un socio por su ficha incompleta; después lo persigue " +
            "recepción.",
        help: "Perseguir no es hostigar: cuando un socio ya recibió esta cantidad de avisos por su " +
            "ficha incompleta, la app deja de insistir y queda en manos de recepción pedirle los " +
            "datos en el mostrador. Con 6 y un recordatorio cada 7 días, la insistencia dura unas " +
            "seis semanas; con 3, unas tres. Se cuentan todos los avisos de este tipo que esa " +
            "persona recibió alguna vez, así que subir el tope reanuda los avisos de quienes ya lo " +
            "habían alcanzado.",
        unit: "avisos",
    }),
    "member.inviteToAppOnActivation": def({
        scope: "global",
        schema: z.boolean(),
        default: true,
        rule: "RN-SOC-08",
        editors: "general",
        label: "Invitar a la app al confirmar el alta",
        description: "Manda solo el mail de bienvenida a la app cuando se confirma el pago del alta; " +
            "apagado, sólo a mano.",
        help: "Prendido, cuando se confirma el pago del alta de un socio le sale solo un mail de " +
            "bienvenida: le dice que ya es socio (con su sede y su plan), qué puede hacer con la app " +
            "y cómo entra: con el mail de su ficha recibe un código de 6 dígitos, sin contraseña. " +
            "Sale una sola vez por socio; si la ficha no tiene mail cargado no sale nada, y si el " +
            "envío falla no se da por enviado. En los dos casos recepción lo manda desde la ficha " +
            "del socio con el botón de invitar, que siempre manda aunque ya se lo haya invitado. " +
            "Apagado, nadie recibe el mail solo: la invitación sale únicamente desde la ficha. " +
            "Tiene sentido apagarlo si prefieren que recepción decida a quién invitar y cuándo, por " +
            "ejemplo mientras la app no esté publicada en las tiendas.",
    }),
    "app.storeLinks": def({
        scope: "global",
        // Vacío es un valor válido: significa «todavía no está en la tienda», y el mail lo dice
        // en vez de inventar un link.
        schema: z.object({
            ios: z.string().url().or(z.literal("")),
            android: z.string().url().or(z.literal("")),
        }),
        default: { ios: "", android: "" },
        rule: "RN-SOC-08",
        editors: "general",
        label: "Enlaces de la app en las tiendas",
        description: "Enlaces de descarga de la app para el mail de bienvenida; vacíos, el mail avisa que " +
            "llegan después.",
        help: "Son los dos enlaces de descarga de la app del socio que van en el mail de bienvenida: " +
            "uno para iPhone (App Store) y otro para Android (Google Play). Se cargan con sus " +
            'nombres y la dirección completa, con https://: {"ios": "https://apps.apple.com/...", ' +
            '"android": "https://play.google.com/..."}; una dirección incompleta no se acepta. El ' +
            "que esté cargado aparece como botón en el mail; si los dos están vacíos, el mail dice " +
            "que el enlace se manda cuando la app esté disponible en las tiendas y que mientras " +
            "tanto consulten en recepción: nunca se inventa un enlace. Cargalos el día que la app " +
            "se publique: los mails que salgan desde entonces ya los llevan, y a los socios " +
            "invitados antes recepción les reenvía la invitación desde la ficha.",
    }),
    "apt.validityMonths": def({
        scope: "global",
        schema: z.number().int().min(1),
        default: 12,
        rule: "RN-SOC-11",
        editors: "general",
        label: "Meses de validez del apto médico",
        description: "Meses que vale un apto médico desde la fecha en que fue emitido.",
        help: "Al cargar o validar un apto, el sistema calcula su vencimiento sumando estos meses a la " +
            "fecha de emisión que figura en el certificado, no a la fecha de carga. Con 12, un apto " +
            "emitido el 15 de marzo vence el 15 de marzo del año siguiente; con 6, el 15 de " +
            "septiembre. Desde ese vencimiento corren los avisos y la gracia. Se aplica a los aptos " +
            "que se carguen o validen de ahí en adelante: los ya validados conservan la fecha que se " +
            "les calculó.",
        unit: "meses",
    }),
    "apt.expiryNoticeDays": def({
        scope: "global",
        schema: z.array(z.number().int().min(0)),
        default: [30, 7],
        rule: "RN-SOC-12",
        pv: "PV-05",
        editors: "general",
        label: "Días de anticipación de los avisos del apto",
        description: "Lista de días antes del vencimiento del apto en que el socio recibe un aviso para " +
            "renovarlo.",
        help: "Se carga como lista entre corchetes, por ejemplo [30, 7]: el socio recibe un aviso " +
            "cuando faltan exactamente esos días para que venza su apto (con [30, 7], uno a los 30 " +
            "días y otro a los 7). Además, siempre y sin configurar nada, avisa el día que vence, al " +
            "día siguiente ya dentro de la gracia y el día que se agota la gracia. A un menor le " +
            "llega al adulto responsable. Con [45, 15, 3] hay tres avisos previos; con [] no hay " +
            "ninguno anticipado, pero los otros hitos salen igual. El número más alto de la lista " +
            "marca además desde cuándo el apto figura como «por vencer» en la ficha del socio.",
        unit: "días",
    }),
    "plans.expiryNoticeDays": def({
        scope: "global",
        schema: z.number().int().min(1).max(60),
        default: 7,
        rule: "RN-PLA-38",
        editors: "financial",
        label: "Aviso de vencimiento de un plan",
        description: "Cuántos días antes de que venza la oferta de un plan se avisa por mail al Dueño y a " +
            "Administración.",
        help: "Un plan puede tener una fecha «se ofrece hasta» (por ejemplo, los planes de octubre " +
            "hasta el 31/10). Con 7, el Dueño y Administración reciben un mail 7 días antes («el " +
            "plan vence el 31/10, actualizalo») y otro el día que vence. Desde estos días la tarjeta " +
            "del plan en Planes y precios se ve en ámbar. Es sólo un aviso: un plan vencido se sigue " +
            "vendiendo y cobrando con el precio de la lista vigente hasta que lo actualicen.",
        unit: "días",
    }),
    "visitor.verificationMonths": def({
        scope: "global",
        schema: z.number().int().min(1),
        default: 12,
        rule: "RN-SOC-40",
        pv: "PV-32",
        editors: "general",
        label: "Meses de validez de la verificación del visitante",
        description: "Meses que vale la verificación de vigencia y apto de un visitante por convenio hecha en " +
            "recepción.",
        help: "Los visitantes por convenio (SportClub, Wellhub) no cargan el apto acá: en su primer " +
            "ingreso, recepción comprueba en la app de su plataforma que tengan membresía vigente y " +
            "apto al día, y marca la ficha como verificada. Esa marca vence a los tantos meses de la " +
            "fecha en que se hizo; vencida, el molinete no lo deja entrar («convenio no vigente») y " +
            "no puede reservar clases hasta que recepción vuelva a verificarlo. Con 12, una " +
            "verificación del 10 de abril vale hasta el 10 de abril del año siguiente; con 6, hasta " +
            "el 10 de octubre. Al verificar, recepción puede escribir a mano otra fecha de " +
            "vencimiento; este valor es el que se aplica cuando no lo hace.",
        unit: "meses",
    }),
    "merge.undoDays": def({
        scope: "global",
        schema: z.number().int().min(1),
        default: 30,
        rule: "RN-SOC-43",
        editors: "general",
        label: "Días para deshacer una fusión de fichas",
        description: "Ventana, desde que se fusionan dos fichas duplicadas, en que la fusión todavía se puede " +
            "deshacer.",
        help: "Cuando Administración fusiona dos fichas de la misma persona (por ejemplo, un socio que " +
            "la migración trajo dos veces), la ficha absorbida queda archivada y su historial pasa a " +
            "la que sobrevive. Durante estos días se puede deshacer y volver todo a su lugar; pasada " +
            "la ventana, la fusión es definitiva. El plazo se fija en el momento de fusionar: " +
            "cambiarlo no acorta ni alarga las fusiones ya hechas. Con 30, una fusión del 1 de junio " +
            "se puede revertir hasta el 1 de julio; con 90, hasta el 30 de agosto.",
        unit: "días",
    }),
    "freeze.vacationDays": def({
        scope: "global",
        schema: z.number().int().min(1),
        default: 15,
        rule: "RN-PLA-19",
        pv: "PV-13",
        editors: "general",
        label: "Días de congelamiento por vacaciones",
        description: "Tope de días por período de plan que un socio semestral o anual puede congelar por " +
            "vacaciones.",
        help: "Los planes semestral y anual incluyen días de congelamiento por vacaciones: el plan se " +
            "pausa, el vencimiento se corre por los días usados y el socio no puede reservar clases " +
            "mientras dura. Este parámetro es el tope general; cada plan tiene además sus propios " +
            "días, y vale el menor de los dos. Los días se pueden usar en uno o más tramos mientras " +
            "la suma no supere el saldo del período de plan, y para pedirlos el socio no puede tener " +
            "deuda. Con 15, un socio anual puede pedir 10 días en enero y 5 en julio; si pide 20, el " +
            "sistema lo rechaza y le dice cuántos le quedan. Bajarlo recorta el beneficio en todos " +
            "los planes que congelan; subirlo sólo tiene efecto si los planes también tienen más " +
            "días cargados.",
        unit: "días",
    }),
    "freeze.vacationNoticeDays": def({
        scope: "global",
        schema: z.number().int().min(0),
        default: 7,
        rule: "RN-PLA-19",
        pv: "PV-13",
        editors: "general",
        label: "Anticipación para pedir el congelamiento",
        description: "Con cuántos días de anticipación, como mínimo, el socio tiene que pedir el " +
            "congelamiento por vacaciones.",
        help: "El congelamiento por vacaciones se pide desde la app o en recepción indicando desde qué " +
            "día empieza. Si entre el día del pedido y el inicio hay menos días que este valor, el " +
            "sistema lo rechaza y le dice al socio con cuántos días lo pidió y cuántos hacían falta. " +
            "Con 7, para congelar desde el 20 hay que pedirlo el 13 o antes; con 0 se puede pedir " +
            "para el mismo día. Un plazo largo permite prever las clases; uno corto es más cómodo " +
            "para el socio que decide a último momento. Sólo aplica al congelamiento por vacaciones: " +
            "el especial (por una lesión, por ejemplo) lo autoriza el Dueño con motivo y no tiene " +
            "anticipación.",
        unit: "días",
    }),
    // ── Clases y reservas (AF §5) ─────────────────────────────────────────────
    "booking.horizonDays": def({
        scope: "global",
        schema: z.number().int().min(1),
        default: 7,
        rule: "RN-CLA-13",
        pv: "PV-22",
        editors: "general",
        label: "Días de anticipación para reservar una clase",
        description: "Hasta cuántos días hacia adelante un socio puede reservar una clase suelta.",
        help: "Marca hasta qué fecha la app deja reservar clases sueltas: el socio ve y puede anotarse en " +
            "las clases que empiezan dentro de esos días contados desde el momento en que reserva; si " +
            "intenta más adelante, la app le pide que vuelva más cerca de la fecha. Con 7, un lunes a " +
            "las 10:00 se puede reservar hasta el lunes siguiente a esa hora. Subirlo (a 14) da más " +
            "previsibilidad al socio pero llena las clases con más anticipación y deja menos margen " +
            "para cambiar la grilla; bajarlo (a 3) reparte mejor los lugares entre quienes reservan " +
            "sobre la fecha. Los lugares fijos no dependen de este plazo: sus reservas se generan solas.",
        unit: "días",
    }),
    "booking.cancelHours": def({
        scope: "global",
        schema: z.number().int().min(0),
        default: 2,
        rule: "RN-CLA-33",
        pv: "PV-21",
        editors: "general",
        label: "Horas de anticipación para cancelar sin falta",
        description: "Hasta cuántas horas antes del inicio una cancelación es en término y no cuenta como falta.",
        help: "Si el socio cancela antes de ese límite, la cancelación es en término: no cuenta como falta, " +
            "el lugar se ofrece a la lista de espera y, en las actividades con cupo semanal, conserva el " +
            "crédito para usarlo esa misma semana. Si cancela después, cuenta como falta para la sanción " +
            "por faltas acumuladas. Con 2, una clase de las 19:00 se cancela sin consecuencias hasta las " +
            "17:00; con 0, cualquier cancelación hecha antes del inicio queda en término. Es el valor " +
            "general del club: cada actividad puede tener un plazo propio, cargado en la actividad, que " +
            "pisa a este. Cambiarlo no toca las faltas ya registradas.",
        unit: "horas",
    }),
    "penalty.faults": def({
        scope: "global",
        schema: z.object({
            count: z.number().int().min(1),
            windowDays: z.number().int().min(1),
            blockDays: z.number().int().min(1),
        }),
        default: { count: 3, windowDays: 30, blockDays: 7 },
        rule: "RN-CLA-34",
        pv: "PV-21",
        editors: "general",
        label: "Sanción por faltas acumuladas",
        description: "Cuántas faltas en cuántos días dejan al socio, por cuántos días, reservando sólo el mismo día.",
        help: "Una falta es cancelar fuera de término o no presentarse a una clase reservada (llegar " +
            "después de la tolerancia también cuenta). Cuando el socio junta la cantidad de faltas " +
            "indicada dentro de la ventana de días, queda sancionado: durante los días de bloqueo sólo " +
            "puede reservar clases del mismo día, si hay lugar. Le llega un aviso con el vencimiento, lo " +
            "ve en «Mis clases», y no afecta ni el ingreso al gimnasio ni sus lugares fijos; desde el " +
            "portal se puede levantar con motivo. Se carga con tres partes: count (faltas), windowDays " +
            "(días corridos en que se cuentan) y blockDays (días que dura la sanción). Con 3, 30 y 7, la " +
            "tercera falta en 30 días bloquea la reserva anticipada por una semana. Bajar count o subir " +
            "windowDays hace la regla más estricta; subir blockDays alarga la sanción.",
    }),
    "fixed.faults": def({
        scope: "global",
        schema: z.object({
            count: z.number().int().min(1),
            windowDays: z.number().int().min(1),
            blockDays: z.number().int().min(1),
        }),
        default: { count: 2, windowDays: 15, blockDays: 14 },
        rule: "RN-CLA-22",
        pv: "PV-24 / PV-29",
        editors: "general",
        label: "Faltas que hacen caer un lugar fijo",
        description: "Cuántas faltas en cuántos días hacen perder el lugar fijo en una clase, y por cuántos días " +
            "la bloquean.",
        help: "Vale sólo para las clases donde el socio tiene lugar fijo, y ahí cuenta como falta también " +
            "la cancelación hecha en término: el lugar fijo se sostiene con asistencia real. Al juntar " +
            "la cantidad de faltas dentro de la ventana de días, la fijación cae, el lugar pasa al " +
            "siguiente de la cola de esa clase, se le avisa al socio y no puede volver a fijarse ni " +
            "reservar suelto esa clase durante los días de bloqueo. Se carga con tres partes: count " +
            "(faltas), windowDays (días corridos en que se cuentan) y blockDays (días de bloqueo sobre " +
            "esa clase). Con 2, 15 y 14, la segunda falta en 15 días hace caer el fijo y bloquea esa " +
            "clase por dos semanas. Es independiente de la sanción por faltas acumuladas, que se " +
            "configura aparte.",
    }),
    "fixed.restitutionDays": def({
        scope: "global",
        schema: z.number().int().min(0),
        default: 7,
        rule: "RN-CLA-22bis",
        pv: "PV-27",
        editors: "general",
        label: "Días de gracia para los lugares fijos",
        description: "Si vence el plan que permite fijar clases, cuántos días tiene el socio para renovar y " +
            "recuperar sus fijos.",
        help: "Cuando a un socio se le vence el plan que le permite tener clases fijas, sus fijaciones " +
            "quedan en pausa: dejan de generarle reservas y se le avisa. Si renueva un plan que habilite " +
            "fijar dentro de estos días, las recupera solas, tal cual estaban. Pasado el plazo, las " +
            "pierde y cada lugar pasa al siguiente de la cola de esa clase. La revisión corre una vez " +
            "por día, a la madrugada. Con 7, un plan que vence el 1 conserva los fijos en pausa hasta el " +
            "8; con 0, se pierden en la revisión del día siguiente. Subirlo (a 15) da más margen a quien " +
            "se atrasa en pagar, a costa de tener lugares en pausa que nadie más puede tomar.",
        unit: "días",
    }),
    "event.registrationExpiryHours": def({
        scope: "global",
        schema: z.number().int().min(1),
        default: 48,
        rule: "RN-CLA-42",
        editors: "general",
        label: "Plazo para pagar la inscripción a un evento",
        description: "Horas que una inscripción a un evento pago puede quedar sin pagar antes de vencer y liberar el lugar.",
        help: "La inscripción a una clase master o a los Viva Games queda confirmada recién cuando se " +
            "paga; mientras tanto ocupa un lugar del cupo. Si pasan estas horas desde que se anotó y " +
            "no pagó, la inscripción vence sola, el cobro pendiente se anula y el lugar vuelve a " +
            "estar disponible para otro. La revisión corre cada 15 minutos. Con 48, alguien que se " +
            "anota el lunes a las 10 y no paga pierde el lugar el miércoles a partir de las 10. " +
            "Subirlo da más margen para pagar, a costa de tener lugares retenidos por gente que " +
            "quizás no venga; bajarlo libera el cupo más rápido en eventos muy pedidos.",
        unit: "horas",
    }),
    "waitlist.windowMin": def({
        scope: "global",
        schema: z.number().int().min(1),
        default: 15,
        rule: "RN-CLA-28",
        pv: "PV-23",
        editors: "general",
        label: "Tiempo para confirmar un lugar liberado",
        description: "Minutos que tiene el primero de la lista de espera para confirmar un lugar que se liberó.",
        help: "Cuando se libera un lugar en una clase llena, se le ofrece por aviso al primero de la lista " +
            "de espera y arranca esta cuenta regresiva; a mitad de camino recibe un recordatorio. Si " +
            "confirma, la reserva queda hecha (y si le corresponde un extra, lo paga en el acto). Si no " +
            "responde o rechaza, la oferta vence y pasa sola al siguiente. La ventana nunca va más allá " +
            "del inicio de la clase: si faltan 10 minutos, tiene 10. Con 15, un lugar liberado a las " +
            "16:00 se puede confirmar hasta las 16:15. Subirlo da más tiempo a cada uno pero hace más " +
            "lenta la cadena cuando hay varios anotados; bajarlo la acelera, a costa de que a alguien " +
            "se le pase la oferta.",
        unit: "minutos",
    }),
    "waitlist.simultaneousMin": def({
        scope: "global",
        schema: z.number().int().min(1),
        default: 60,
        rule: "RN-CLA-29",
        pv: "PV-23",
        editors: "general",
        label: "Oferta a toda la lista cerca del inicio",
        description: "A menos de estos minutos del inicio, un lugar liberado se ofrece a toda la lista de espera " +
            "a la vez.",
        help: "Cerca del inicio no hay tiempo para ofrecer el lugar de a uno. Cuando faltan menos minutos " +
            "que este valor, el lugar liberado se avisa a todos los anotados al mismo tiempo y se lo " +
            "queda el primero que confirma; los demás vuelven a la lista hasta el inicio de la clase, " +
            "cuando la lista se cierra. Con 60, un lugar que se libera a las 18:20 para una clase de " +
            "las 19:00 se ofrece a toda la lista; uno liberado a las 17:30 se ofrece de a uno, con la " +
            "ventana de confirmación. Subirlo hace que más lugares se disputen entre todos (más rápido, " +
            "menos ordenado); bajarlo respeta más el orden de la lista pero arriesga que el lugar quede " +
            "vacío.",
        unit: "minutos",
    }),
    "class.checkinWindowMinBefore": def({
        scope: "global",
        schema: z.number().int().min(0),
        default: 30,
        rule: "RN-CLA-30 / RN-CLA-31",
        editors: "general",
        label: "Apertura del presente antes de la clase",
        description: "Desde cuántos minutos antes del inicio el socio puede marcar presente con el QR del salón.",
        help: "El presente lo marca el propio socio escaneando el QR del salón desde la app; el ingreso " +
            "por el punto de acceso también lo marca cuando tiene reserva en una clase que arranca " +
            "dentro de esta ventana. Antes de este margen la app rechaza el escaneo y le dice a qué hora " +
            "abre; el presente se acepta desde ese momento hasta la tolerancia de llegada. Con 30, una " +
            "clase de las 19:00 admite el presente desde las 18:30. Subirlo (a 60) ayuda cuando los " +
            "socios llegan temprano al local; bajarlo evita que alguien marque presente y se vaya antes " +
            "de la clase. Con clases seguidas en el mismo salón, conviene que este margen no pise la " +
            "tolerancia de la clase anterior: el escaneo se anotaría en esa.",
        unit: "minutos",
    }),
    "class.consolidationMin": def({
        scope: "global",
        schema: z.number().int().min(0),
        default: 15,
        rule: "RN-CLA-32",
        pv: "PV-92",
        editors: "general",
        label: "Tolerancia de llegada a la clase",
        description: "Minutos de iniciada la clase hasta los que se acepta el presente; en ese minuto la lista " +
            "se cierra sola.",
        help: "Hasta ese minuto de iniciada la clase el socio puede marcar presente con el QR, y el " +
            "profesor puede marcar a mano a quien no pudo escanear y sumar a alguien sin reserva si hay " +
            "lugar. En ese minuto el sistema cierra la lista solo, aunque nadie haya hecho nada: los que " +
            "no marcaron quedan ausentes y esa ausencia cuenta como falta. Después, el QR rechaza el " +
            "ingreso a la clase y cualquier corrección la hace el coordinador con motivo. Con 15, en una " +
            "clase de las 19:00 se puede marcar presente hasta las 19:15; con 0, la lista se cierra al " +
            "minuto del inicio. La asistencia cerrada alimenta las faltas, los lugares fijos y la " +
            "liquidación de los profesores, así que conviene cambiarlo con la coordinación avisada.",
        unit: "minutos",
    }),
    "week.start": def({
        scope: "global",
        schema: z.enum(["monday", "sunday"]),
        default: "monday",
        rule: "RN-CLA-23 / RN-ACC-11",
        pv: "PV-28",
        editors: "general",
        label: "Día en que empieza la semana",
        description: "Con qué día arranca la semana para los cupos semanales por actividad y los créditos de " +
            "recupero.",
        help: "Define qué semana se usa para contar cuántas clases de una actividad reservó el socio (por " +
            "ejemplo «2 Pole por semana», sumando todas las sedes), hasta cuándo vale un crédito de " +
            "recupero y la vista semanal de los profesores. Con monday la semana va de lunes a domingo " +
            "y los contadores arrancan de cero el lunes a las 0:00; con sunday, de domingo a sábado. Se " +
            "escribe tal cual, en minúsculas: monday o sunday. Cambiarlo mueve los contadores de todos " +
            "los socios de golpe, así que conviene cargarlo con fecha de vigencia en el primer día de " +
            "la semana nueva. La frecuencia semanal de ingresos que controla el molinete hoy se cuenta " +
            "siempre de lunes a domingo, sin importar este valor.",
    }),
    // ── Profesores y liquidación (AF §7) ──────────────────────────────────────
    "payroll.timeclockToleranceMin": def({
        scope: "global",
        schema: z.number().int().min(0),
        default: 10,
        rule: "RN-PRO-08",
        pv: "PV-56",
        editors: "general",
        label: "Tolerancia del fichaje contra el turno",
        description: "Minutos de diferencia entre la fichada y el turno que se perdonan: dentro de eso se paga el turno completo.",
        help: "Cada fichada del personal se cruza con el turno que tenía planificado. Si la diferencia (llegar tarde, " +
            "irse antes o quedarse de más) no supera esta tolerancia, la hora se redondea al turno: se paga lo " +
            "planificado, ni un minuto menos ni uno más. Si la supera, la fichada queda marcada como discrepancia y no " +
            "se liquida sola: se computa lo menor entre lo planificado y lo trabajado, y la resuelve el coordinador con " +
            "un motivo en la pre-liquidación (la hora extra sólo se paga si él la aprueba). Ejemplo: con 10, quien " +
            "llega 6 minutos tarde cobra el turno entero; quien llega 15 tarde queda marcado. Subirlo perdona más " +
            "impuntualidad sin revisión; bajarlo a 0 manda a revisión cualquier minuto de diferencia.",
        unit: "minutos",
    }),
    "payroll.multipliers": def({
        scope: "global",
        schema: z.object({
            sunday: z.number().positive(),
            holiday: z.number().positive(),
            saturday: z.number().positive(),
        }),
        default: { sunday: 2, holiday: 2, saturday: 1.5 },
        rule: "RN-PRO-05",
        pv: "PV-43 / PV-54",
        editors: "financial",
        label: "Recargos por sábado, domingo y feriado",
        description: "Por cuánto se multiplica la hora trabajada un sábado, un domingo o un feriado. No se acumulan: rige el mayor.",
        help: "Tiene tres partes: sunday (domingo), holiday (feriado) y saturday (sábado); cada una es el factor que " +
            "multiplica la tarifa de la hora ese día. Con 2 la hora vale el doble; con 1.5, una vez y media; con 1 no " +
            "hay recargo. Si un feriado cae en domingo se aplica el mayor de los dos, nunca el producto: con domingo 2 y " +
            "feriado 2 paga ×2, no ×4. Ojo: este parámetro es sólo el valor con el que nace cada versión nueva del " +
            "tarifario, y cada tarifario guarda su propia copia; cambiarlo acá no toca el tarifario vigente ni los " +
            "meses ya liquidados. Para que rija hay que publicar un tarifario nuevo desde Profesores → Tarifario. Se " +
            'carga como texto con las tres partes, por ejemplo {"sunday": 2, "holiday": 2, "saturday": 1.5} ' +
            "(el decimal va con punto).",
        unit: "veces la tarifa",
    }),
    "payroll.trialConversionDays": def({
        scope: "global",
        schema: z.number().int().min(1).max(365),
        default: 30,
        rule: "RN-PRO-20",
        pv: "PV-49",
        editors: "general",
        label: "Ventana para acreditar una prueba convertida",
        description: "Días corridos entre la clase de prueba y el alta del socio para que el profe que la dictó cobre una hora más.",
        help: "Cuando alguien toma una clase de prueba y después se asocia, el profesor que dictó esa prueba cobra una " +
            "hora adicional a tarifa base, sin premio ni recargos. El sistema lo cruza solo por DNI: acredita la hora " +
            "si el alta ocurre dentro de estos días corridos desde la prueba; si pasó más tiempo, no la acredita. Si " +
            "hubo más de una prueba, cuenta la última anterior al alta. La hora se liquida en el mes del alta, aunque " +
            "la prueba haya sido en un mes ya cerrado. Ejemplo: con 30, una prueba del 3 de marzo y un alta del 28 de " +
            "marzo acreditan la hora; un alta del 10 de abril ya no. Subirlo reconoce conversiones más lentas; " +
            "bajarlo exige que la persona se asocie más rápido.",
        unit: "días",
    }),
    "payroll.personalTrainingMinWeeklyClasses": def({
        scope: "global",
        schema: z.number().int().min(0),
        default: 4,
        rule: "RN-PRO-22",
        editors: "general",
        label: "Clases semanales mínimas para tomar personalizados",
        description: "Cuántas clases por semana en la grilla publicada necesita un profe para poder tomar alumnos personalizados.",
        help: "Sólo los profesores con al menos esta cantidad de clases semanales en la grilla vigente pueden tomar " +
            "alumnos personalizados. El sistema lo recalcula cada vez que se publica una grilla: si un cambio deja a " +
            "un profe por debajo, lo inhabilita para tomar nuevos personalizados y avisa al coordinador; los alumnos " +
            "que ya tenía no se dan de baja solos, los resuelve el coordinador a mano. Ejemplo: con 4, un profe que " +
            "pasa de 4 a 3 clases por semana queda inhabilitado hasta volver a 4. Con 0, cualquier profe activo puede " +
            "tomar personalizados.",
        unit: "clases por semana",
    }),
    "payroll.noStudentsConfirmHours": def({
        scope: "global",
        schema: z.number().int().min(1),
        default: 24,
        rule: "RN-PRO-17",
        editors: "general",
        label: "Plazo para confirmar una clase sin alumnos",
        description: "Horas desde el fin de la clase en que el profe puede marcarla «dictada sin alumnos» desde su portal.",
        help: "Una clase que terminó con cero presentes queda «a confirmar»: el sistema no sabe si el profe estuvo y no " +
            "vino nadie (se paga igual, a tarifa base) o si el profe faltó (no se paga). Dentro de este plazo, contado " +
            "desde la hora de fin de la clase, el profesor la marca «dictada sin alumnos» desde su portal. Vencido el " +
            "plazo, el portal se lo rechaza y sólo puede marcarla el coordinador en la pre-liquidación. En los dos " +
            "casos el coordinador tiene que validarla: sin esa validación la clase no se liquida. Ejemplo: con 24, una " +
            "clase que terminó el lunes a las 20 se puede confirmar hasta el martes a las 20. Subirlo le da más margen " +
            "al profe; bajarlo lo obliga a hacerlo en el día.",
        unit: "horas",
    }),
    "teachers.barRounding": def({
        scope: "global",
        schema: z.enum(["floor", "round", "ceil"]),
        default: "floor",
        rule: "RN-PRO-12",
        editors: "general",
        label: "Redondeo del promedio que fija la vara de premio",
        description: "Cómo se redondea a un número entero el promedio de presentes de una clase al cerrar el mes, para fijar la vara del mes siguiente.",
        help: "La vara de premio de cada clase de la grilla es el promedio de presentes de las clases que se dictaron ese " +
            "mes. Como la vara es un número entero de alumnos, el promedio se redondea: con floor se redondea hacia " +
            "abajo, con round al entero más cercano y con ceil hacia arriba. Ejemplo: una clase de lunes 8:00 con 8, " +
            "10, 12, 6 y 5 presentes promedia 8,2: con floor o round la vara queda en 8; con ceil, en 9. El promedio " +
            "sólo mueve la vara si la supera: nunca la baja. Hacia abajo es lo más favorable para el profe y es el " +
            "valor por defecto. Se escribe exactamente floor, round o ceil; el cambio rige desde el próximo cierre de mes.",
    }),
    "payroll.goalLockDay": def({
        scope: "global",
        schema: z.number().int().min(1).max(28),
        default: 5,
        rule: "RN-PRO-23",
        editors: "financial",
        label: "Día tope para editar el objetivo de recepción",
        description: "Hasta este día del mes el objetivo de venta de cada recepcionista se cambia libremente; después, con motivo.",
        help: "A principio de mes se carga el objetivo de venta de planes de cada recepcionista. Hasta este día del mes " +
            "en curso el objetivo se puede corregir sin más; pasado ese día (o si el mes ya terminó), cambiarlo exige " +
            "un motivo obligatorio y queda registrado como excepción auditada. La carga inicial no se bloquea nunca: " +
            "el tope aplica sólo cuando ya hay un objetivo cargado, y los objetivos de meses futuros se editan libres. " +
            "Ejemplo: con 5, el objetivo de septiembre se edita libre hasta el 5 de septiembre; el 6 ya pide motivo. " +
            "Subirlo da más días para acomodar objetivos; bajarlo los congela antes. Se admite hasta el día 28.",
        unit: "día del mes",
    }),
    "commission.basis": def({
        scope: "global",
        schema: z.enum(["all_plans", "long_plans"]),
        default: "all_plans",
        rule: "RN-PRO-25",
        pv: "PV-51",
        editors: "financial",
        label: "Qué ventas cuentan para la comisión de recepción",
        description: "Si la comisión se calcula sobre todas las ventas de planes del recepcionista o sólo sobre los planes largos.",
        help: "Define sobre qué ventas se aplica el porcentaje de comisión de cada recepcionista, y también qué ventas " +
            "suman para el avance de su objetivo. Con all_plans cuentan todas las cuotas de plan cobradas (mensuales y " +
            "cuotas de planes trimestrales, semestrales y anuales). Con long_plans cuentan sólo las cuotas de planes " +
            "largos. En los planes largos la venta se le atribuye a quien vendió el plan aunque el pago entre por otra " +
            "sede; las ventas hechas 100 % desde la app, sin nadie en recepción, no cuentan para nadie. Ejemplo: un " +
            "recepcionista que cobró $500.000 en cuotas mensuales y $300.000 en cuotas de un plan anual suma $800.000 " +
            "con all_plans y $300.000 con long_plans. Se escribe exactamente all_plans o long_plans; cambiarlo a " +
            "mitad de mes cambia el avance de todos desde el próximo cálculo.",
    }),
    "commission.scale": def({
        scope: "global",
        schema: z.array(commissionTier).min(1),
        // Lectura (a) de RN-PRO-25: 4 tramos 2 / 3 / 4,5 / 7,8 %.
        default: [
            { thresholdPct: 0, commissionPct: 2 },
            { thresholdPct: 70, commissionPct: 3 },
            { thresholdPct: 100, commissionPct: 4.5 },
            { thresholdPct: 130, commissionPct: 7.8 },
        ],
        rule: "RN-PRO-25",
        pv: "PV-50 / PV-51",
        editors: "financial",
        label: "Escala de comisión de recepción",
        description: "Tramos: a partir de qué % del objetivo alcanzado se paga qué % de comisión sobre las ventas de planes del mes.",
        help: "Es una lista de tramos; cada uno tiene thresholdPct (el porcentaje del objetivo a partir del cual aplica) " +
            "y commissionPct (el porcentaje de comisión sobre las ventas de planes del mes). Se toma el tramo de mayor " +
            "umbral que el recepcionista haya alcanzado; si no llega a ninguno, se usa el de umbral más bajo. Con la " +
            "escala por defecto: hasta 69 % del objetivo paga 2 %; de 70 a 99 % paga 3 %; de 100 a 129 % paga 4,5 %; " +
            "130 % o más paga 7,8 %. Ejemplo: objetivo de $1.000.000 y ventas por $1.100.000 = 110 % alcanzado, " +
            "comisión del 4,5 % sobre $1.100.000 = $49.500. Cada objetivo guarda una copia de la escala al crearse: " +
            "cambiarla acá rige para los objetivos que se carguen de ahí en adelante, no para los meses en curso ni " +
            'los cerrados. Se carga como texto, por ejemplo [{"thresholdPct": 0, "commissionPct": 2}, ' +
            '{"thresholdPct": 70, "commissionPct": 3}]; hace falta al menos un tramo y el decimal va con punto.',
        unit: "%",
    }),
    // ── Contable y ARCA (AF §9) ───────────────────────────────────────────────
    "reports.activityCommonCostsAllocation": def({
        scope: "global",
        schema: z.enum(["none", "by_present", "by_revenue"]),
        default: "none",
        rule: "RN-CON-18bis / RN-REP-14",
        pv: "PV-109",
        editors: "financial",
        label: "Reparto de costos comunes por actividad",
        description: "Si los costos comunes de la sede se reparten entre las actividades en el resultado por actividad, y cómo.",
        help: "El resultado por actividad muestra, para cada disciplina, sus ingresos atribuidos menos sus costos " +
            "directos (horas de clase liquidadas y gastos cargados con esa actividad): ese es el margen de " +
            "contribución. Los costos comunes de la sede (alquiler, recepción, limpieza: todo gasto aprobado que no " +
            "se cargó con una actividad) no se reparten con none, y la columna de comunes queda vacía. Con " +
            "by_present se reparten según los presentes de cada actividad en el mes y con by_revenue según los " +
            "ingresos atribuidos a cada una; en los dos casos la columna es informativa y el margen de contribución " +
            "no cambia. Ejemplo: $900.000 de comunes con 600 presentes de Funcional y 300 de Pilates se reparten " +
            "$600.000 y $300.000 con by_present. Se ajusta desde Resultado por sede, solapa Por actividad, y lo " +
            "decide el Dueño.",
    }),
    "arca.invoiceAllCash": def({
        scope: "global",
        schema: z.boolean(),
        default: false,
        rule: "RN-CON-04 / RN-CON-27",
        pv: "PV-68",
        editors: "financial",
        label: "Facturar también los cobros en efectivo",
        description: "Apagado: el efectivo se factura a pedido; encendido: todo cobro genera su comprobante al confirmarse.",
        help: "Los cobros electrónicos (débito automático, posnet, QR, transferencia conciliada, pago desde la app) " +
            "generan su factura solos al confirmarse. Con este parámetro apagado, un cobro en efectivo no factura " +
            "hasta que alguien aprieta Facturar sobre el pago o el socio lo pide desde la app; encendido, el " +
            "efectivo también factura solo en el mismo momento del cobro, en recepción y en el kiosko. Hoy el club " +
            "factura alrededor del 40 % de lo que cobra: prenderlo lleva la facturación al 100 % de los cobros y " +
            "cambia la carga fiscal, así que es una decisión del Dueño con el contador, no un ajuste operativo. " +
            "Se puede prender con fecha de vigencia futura para arrancar un mes limpio.",
    }),
    "arca.finalConsumerCap": def({
        scope: "global",
        schema: moneyInt.min(0),
        // Tope de ARCA para el consumidor final NO identificado. Es un valor que cambia por
        // resolución del organismo: por eso es parámetro y no una constante del código.
        default: 34_400_000, // $344.000,00
        rule: "RN-CON-02",
        editors: "financial",
        label: "Tope de Factura B sin identificar al socio",
        description: "Importe a partir del cual una Factura B a consumidor final exige el DNI del receptor antes de emitirse.",
        help: "Es el tope que fija ARCA para facturar a un consumidor final sin identificarlo. Cuando una Factura B " +
            "supera este importe, el sistema no la emite hasta tener el DNI del receptor: lo toma de la ficha del " +
            "socio y sólo pide confirmarlo; si la ficha no tiene documento, la emisión se frena con un aviso. Con el " +
            "valor actual, un plan anual en un pago de $400.000 pide DNI y una cuota mensual de $60.000 no. El " +
            "importe se carga en centavos, sin puntos ni coma: $344.000,00 se escribe 34400000. Se actualiza sólo " +
            "cuando ARCA cambia el tope por resolución: bajarlo por debajo del oficial agrega pedidos de DNI " +
            "innecesarios, y subirlo por encima expone a que ARCA rechace el comprobante.",
        unit: "$",
    }),
    "arca.issuer": def({
        scope: "global",
        schema: z.object({
            businessName: z.string(),
            cuit: z.string(),
            fiscalCondition: z.string(),
            grossIncome: z.string(),
            activityStartDate: z.string(),
            address: z.string(),
        }),
        // Datos societarios que van IMPRESOS en el PDF reglamentario (RN-CON-01). El CUIT real
        // y el certificado se cargan en la puesta en marcha; la clave privada NUNCA vive acá.
        default: {
            businessName: "Agua Viva Recreación y Deporte SRL",
            cuit: "",
            fiscalCondition: "IVA Responsable Inscripto",
            grossIncome: "",
            activityStartDate: "",
            address: "",
        },
        rule: "RN-CON-01",
        editors: "financial",
        label: "Datos del club impresos en las facturas",
        description: "Razón social, CUIT, condición ante el IVA, ingresos brutos, inicio de actividades y domicilio del comprobante.",
        help: "Es el encabezado del emisor que se imprime en el PDF de cada comprobante: businessName (razón social), " +
            "cuit, fiscalCondition (condición frente al IVA), grossIncome (número de Ingresos Brutos), " +
            "activityStartDate (fecha de inicio de actividades, tal como querés que se lea) y address (domicilio " +
            "fiscal). Lo que esté vacío sale impreso como un guion, así que conviene completarlo antes de emitir la " +
            "primera factura; un CUIT mal cargado acá sale mal en todos los comprobantes desde ese momento. Son " +
            "sólo datos impresos: el certificado fiscal y la clave con la que se firma ante ARCA se cargan aparte, " +
            "en Integraciones, y nunca acá. Un cambio se puede dejar con fecha de vigencia futura, por ejemplo un " +
            "cambio de domicilio a partir del mes que viene.",
    }),
    "accountant.export": def({
        scope: "global",
        schema: z.object({
            email: z.string().email().or(z.literal("")),
            dayOfMonth: z.number().int().min(1).max(28),
        }),
        default: { email: "", dayOfMonth: 5 },
        rule: "RN-CON-10",
        pv: "PV-70",
        editors: "financial",
        label: "Envío mensual al contador",
        description: "Casilla del contador y día del mes en que se le manda el resumen de los exportes del mes anterior.",
        help: "Una vez por mes, el día indicado en dayOfMonth (entre 1 y 28), el sistema le manda a la casilla email " +
            "el paquete contable del mes anterior con los archivos adjuntos: libro IVA Ventas (también en el " +
            "formato importable de ARCA), libro de gastos, cobros por medio de pago y sede, y ventas de kiosko, con " +
            "un resumen de totales en el cuerpo. Si algún archivo no entra en el límite de tamaño del correo, en su " +
            "lugar va un link de descarga que vence a los días indicados en «Vencimiento de los links del paquete " +
            "al contador». Con el valor de fábrica, el 5 de mayo sale lo de abril. Si la casilla está vacía no se " +
            "manda nada a ninguna dirección adivinada y queda registrado como no enviado. Se edita más cómodo desde " +
            "Exportes contables, en Envío mensual al contador, que es esta misma configuración.",
    }),
    "accountant.exportLinkDays": def({
        scope: "global",
        // Hasta 7: es el máximo que permite una URL firmada de S3.
        schema: z.number().int().min(1).max(7),
        default: 7,
        rule: "RN-CON-10",
        editors: "financial",
        label: "Vencimiento de los links del paquete al contador",
        description: "Días que dura el link de descarga de un archivo del paquete mensual que no entró adjunto en el correo.",
        help: "El paquete mensual al contador viaja con sus archivos adjuntos. Cuando uno es demasiado grande para el " +
            "correo (pasa con meses de mucho movimiento), en su lugar el mail lleva un link de descarga directa, " +
            "sin usuario ni contraseña, que deja de funcionar a los días indicados acá; el correo dice la fecha y " +
            "hora exactas de vencimiento. Como cualquiera con el link puede bajar el archivo mientras esté vigente, " +
            "conviene no estirarlo: el máximo es 7 días. Vencido el link, el contador baja el mismo archivo desde " +
            "Exportes contables con su usuario.",
        unit: "días",
    }),
    "platform.reconcileDays": def({
        scope: "global",
        schema: z.number().int().min(1),
        default: 10,
        rule: "RN-CON-20",
        editors: "general",
        label: "Día del mes de la liquidación de convenios",
        description: "Día del mes siguiente en que el sistema genera la pre-liquidación de SportClub, Wellhub y otros convenios.",
        help: "Los convenios (SportClub, Wellhub y similares) se liquidan por ingreso: el sistema cuenta las entradas de " +
            "visitantes que registró el control de acceso y arma, para cada convenio activo, la pre-liquidación del " +
            "mes anterior con el detalle por día, sede y visitante y el monto esperado al valor vigente. Ese armado " +
            "corre solo a las 6 de la mañana del día del mes que se indica acá. Ejemplo: con 10, la liquidación de " +
            "agosto se genera el 10 de septiembre. Conviene dejar unos días de margen después del fin de mes para que " +
            "los ingresos estén completos. Si el número no existe en un mes (por ejemplo 31 en un mes de 30 días), " +
            "ese mes no se genera sola: se arma a mano con el botón de Contable → Liquidación de convenios.",
        unit: "día del mes",
    }),
    "stock.expiryAlertDays": def({
        scope: "global",
        schema: z.number().int().min(1),
        default: 15,
        rule: "RN-CON-28",
        pv: "PV-74",
        editors: "general",
        label: "Anticipación del aviso de productos por vencer",
        description: "Con cuántos días de anticipación se avisa a control de stock que hay lotes del kiosko por vencer.",
        help: "Cuando entra mercadería, los productos marcados llevan fecha de vencimiento por lote. Todos los días a las " +
            "8 de la mañana el sistema busca los lotes que vencen dentro de esta cantidad de días y les manda un aviso " +
            "(notificación en el portal, push y mail con el detalle) a los usuarios con rol de control de stock, al " +
            "Dueño y a Administración. Lo que queda de cada lote es una estimación: se descuenta lo vendido en orden " +
            "de vencimiento. Ejemplo: con 15, un lote que vence el 20 de octubre empieza a aparecer en el aviso el 5 " +
            "de octubre. Subirlo da más tiempo para promocionar o mover el producto; bajarlo achica la lista pero " +
            "deja menos margen. Dar de baja lo vencido no es automático: es una merma con motivo.",
        unit: "días",
    }),
    "kiosk.posFeePct": def({
        scope: "global",
        schema: z.number().min(0).max(100),
        default: 0,
        rule: "RN-CON-25",
        editors: "financial",
        label: "Comisión del posnet en las ventas del kiosko",
        description: "Porcentaje que se descuenta de cada venta del kiosko cobrada con el posnet, para mostrar el neto que entra.",
        help: "Las ventas del kiosko muestran el bruto, la comisión y el neto que realmente entra. En las de Mercado " +
            "Pago la comisión la informa Mercado Pago venta por venta; en las de efectivo no hay comisión; en las " +
            "del posnet se usa este porcentaje, porque el posnet no informa la comisión de cada cobro. Con 0 (el " +
            "valor de fábrica), el neto de una venta por posnet es igual al total. Ejemplo: con 3,5, una venta de " +
            "$10.000 por posnet muestra una comisión de $350 y un neto de $9.650. Cada venta guarda el porcentaje " +
            "que regía al cobrarse: cambiarlo acá vale para las ventas que vengan, no reescribe las anteriores. " +
            "No cambia lo que se cobra ni lo que se factura: sólo el neto que se muestra. El decimal va con punto.",
        unit: "%",
    }),
    "stock.countFrequencyDays": def({
        scope: "global",
        schema: z.number().int().min(1),
        default: 7,
        rule: "RN-CON-29",
        pv: "PV-74",
        editors: "general",
        label: "Frecuencia sugerida del conteo físico de stock",
        description: "Cada cuántos días se espera que recepción cuente el stock de su sede. Es una referencia, no un bloqueo.",
        help: "Es la cadencia acordada para que las recepcionistas carguen el conteo físico del kiosko de su sede " +
            "(7 = semanal, 14 = quincenal). El conteo se carga desde Kiosko → Stock y conteo: el sistema muestra la " +
            "diferencia ítem por ítem contra el stock teórico y no ajusta nada por sí solo; el ajuste lo aprueba " +
            "control de stock con un motivo, y el histórico de diferencias por sede queda guardado. Hoy este valor es " +
            "una referencia para el equipo: el sistema no manda un recordatorio ni impide operar si el conteo se " +
            "atrasa, así que cambiarlo no altera ningún cálculo.",
        unit: "días",
    }),
    // ── Avisos y CRM (AF §10) ─────────────────────────────────────────────────
    "notifications.catalog": def({
        scope: "global",
        schema: z.record(z.string(), notificationCatalogEntry),
        // Los 9 avisos de RN-CRM-10, todos activos, con sus plazos default.
        default: {
            due_soon: {
                active: true,
                channels: ["push", "inapp", "email"],
                params: { daysBefore: 3 },
                description: "Cuota por vencer: N días antes y el día del vencimiento.",
            },
            debt: {
                active: true,
                channels: ["push", "inapp", "email"],
                params: { everyDays: 3, maxReminders: 5 },
                description: "Deuda / cuota vencida: al vencer la tolerancia y recordatorios.",
            },
            birthday: {
                active: true,
                channels: ["push", "inapp"],
                params: { hour: 9 },
                description: "Cumpleaños del socio, el día a las 9:00.",
            },
            class_canceled: {
                active: true,
                channels: ["push", "inapp", "email"],
                description: "Clase cancelada: inscriptos, fijos y lista de espera.",
            },
            spot_available: {
                active: true,
                channels: ["push", "inapp"],
                description: "Lugar liberado: le avisa al primero de la lista de espera y le abre su ventana para confirmar.",
            },
            absent_14d: {
                // Apagado por default (27-sep-2026, Damián): con la lógica actual sale TODOS los días
                // mientras el socio siga ausente. Se prende desde el catálogo cuando se revise la cadencia.
                active: false,
                channels: ["push", "inapp", "email"],
                params: { days: 14 },
                description: "Socio con N días sin acceso.",
            },
            trial_scheduled: {
                active: true,
                channels: ["push", "inapp"],
                description: "Clase de prueba agendada: al profesor, y recordatorio el día de la clase.",
            },
            apt_expiring: {
                active: true,
                channels: ["push", "inapp", "email"],
                params: { daysBefore: 30 },
                description: "Apto médico por vencer / vencido / gracia agotada.",
            },
            waitlist_reminder: {
                active: true,
                // RN-CRM-11: todo push tiene su espejo en el centro de avisos.
                channels: ["push", "inapp"],
                description: "Confirmación pendiente de lista de espera, a mitad de la ventana.",
            },
            // Avisos sociales del muro (mejora aprobada el 25-sep-2026). Las claves son las de
            // `NOTICE_TYPES` (enums.ts): el emisor usa exactamente el mismo string.
            wall_approved: {
                active: true,
                channels: ["push", "inapp"],
                description: "Muro: un moderador aprobó la publicación del socio.",
            },
            wall_rejected: {
                active: true,
                channels: ["push", "inapp"],
                description: "Muro: un moderador rechazó la publicación del socio, con el motivo.",
            },
            wall_comment: {
                active: true,
                channels: ["push", "inapp"],
                description: "Muro: comentaron la publicación del socio. Uno por publicación por día; los siguientes suman al mismo aviso.",
            },
            wall_reaction: {
                active: true,
                channels: ["push", "inapp"],
                params: { groupHours: 6 },
                description: "Muro: reacciones a la publicación del socio, agrupadas: como máximo un aviso por publicación cada N horas.",
            },
        },
        rule: "RN-CRM-10",
        pv: "PV-17",
        editors: "general",
        label: "Catálogo de avisos automáticos a socios y profesores",
        description: "Los avisos que el sistema manda solo: cuál está prendido, por qué canales sale y con qué plazos.",
        help: "Son los avisos que salen sin que nadie los mande: cuota por vencer, cuota vencida, cumpleaños, clase " +
            "cancelada, lugar liberado, socio ausente, clase de prueba agendada (al profesor), apto médico por vencer " +
            "y confirmación pendiente de lista de espera; y los cuatro del muro social: publicación aprobada, " +
            "publicación rechazada, comentario nuevo y reacciones (agrupadas cada «groupHours» horas). Cada aviso tiene «active» (apagado, ese aviso no sale por " +
            "ningún canal), «channels» (por dónde sale: push al celular, centro de avisos de la app y/o email) y " +
            "«params», sus plazos. Los plazos que el sistema lee de acá son los de la cuota: «daysBefore» de cuota " +
            "por vencer (3 = avisa cada día desde tres días antes hasta el vencimiento) y «everyDays» y " +
            "«maxReminders» de la deuda (3 y 5 = un recordatorio cada tres días, cinco como máximo, empezando el día " +
            "que se agota la tolerancia); los días de ausencia y del apto se ajustan en sus propios parámetros. Se " +
            "carga como texto estructurado (JSON) completo: cambiá sólo el valor que hace falta y dejá el resto igual.",
    }),
    "notifications.dedupePerDay": def({
        scope: "global",
        schema: z.boolean(),
        default: true,
        rule: "RN-CRM-09",
        editors: "general",
        label: "Un solo aviso del mismo tipo por día",
        description: "Evita que una persona reciba dos veces el mismo tipo de aviso en el mismo día.",
        help: "Prendido, cada persona recibe como máximo un aviso de cada tipo por día: si el sistema intenta " +
            "mandarle dos veces «cuota por vencer» el mismo día, la segunda se descarta sin ruido. Aplica por tipo, " +
            "no en general: el saludo de cumpleaños y el aviso de deuda salen los dos aunque caigan el mismo día. " +
            "Apagado, un socio con dos cuotas por vencer puede recibir dos avisos iguales en la misma mañana. Los " +
            "recordatorios de deuda (cada tantos días) y las alertas de seguridad y de negocio tienen su propio " +
            "ritmo y no dependen de este interruptor; conviene dejarlo prendido.",
    }),
    "retention.coldDays": def({
        scope: "global",
        schema: z.number().int().min(1),
        default: 7,
        rule: "RN-CRM-04",
        editors: "general",
        label: "Días para que un interesado quede frío",
        description: "Días sin ningún contacto registrado tras los que un interesado se resalta como «frío».",
        help: "Cada mañana el sistema revisa los interesados que todavía no se convirtieron ni se perdieron y mira " +
            "cuándo fue el último contacto que se les registró (o la fecha en que se cargaron, si nunca hubo uno). " +
            "Si pasaron más días que este valor, el interesado aparece resaltado como «frío» en la lista para que " +
            "recepción lo retome; en cuanto se le anota un contacto nuevo, la marca se saca sola. Con 7, una " +
            "interesada cargada un lunes sin ningún seguimiento aparece fría a la semana siguiente. Bajalo si querés " +
            "que el equipo insista más rápido; subilo si te marca como fríos a los que hablás cada quince días.",
        unit: "días",
    }),
    "retention.socialPurgeDays": def({
        scope: "global",
        schema: z.number().int().min(1),
        default: 90,
        rule: "RN-CRM-14",
        editors: "general",
        label: "Días que se guarda lo eliminado del muro",
        description: "Días que una publicación eliminada del muro se conserva para revisión antes de borrarse del todo.",
        help: "Cuando un moderador elimina una publicación del muro social, deja de verse pero queda guardada este " +
            "tiempo, por si hay que revisar una denuncia o dar marcha atrás. Cumplido el plazo, se borra de forma " +
            "definitiva y ya no se puede recuperar: es de las pocas cosas que el sistema borra de verdad. Con 90, " +
            "algo eliminado el 1 de marzo desaparece del todo a fines de mayo; con 30, el 31 de marzo. El plazo se " +
            "fija en el momento de eliminar, así que cambiarlo acá no toca lo que ya estaba eliminado; bajarlo " +
            "acorta el margen para revisar y subirlo lo alarga.",
        unit: "días",
    }),
    "retention.terminatedMembersYears": def({
        scope: "global",
        schema: z.number().int().min(1).nullable(),
        default: null,
        rule: "modelo-datos §3.3 (Ley 25.326)",
        editors: "general",
        label: "Años para anonimizar fichas dadas de baja",
        description: "Años que se guarda la ficha de un ex-socio antes de anonimizarla de oficio; null = no se anonimiza sola.",
        help: "La ley de protección de datos permite que un ex-socio pida que se borren sus datos: en ese caso la " +
            "ficha se anonimiza (el nombre queda en iniciales y se borran email, teléfono y dirección) pero los " +
            "pagos y la auditoría se conservan, porque tienen respaldo legal propio. Este valor sería el plazo para " +
            "hacer eso de oficio con todas las fichas dadas de baja, sin que nadie lo pida: con 5, una baja de marzo " +
            "de 2026 se anonimizaría en marzo de 2031. Hoy ningún proceso automático lo aplica: queda registrado " +
            "para cuando el club defina el plazo. Se carga como número de años, o la palabra null para que no haya " +
            "plazo automático.",
        unit: "años",
    }),
    // ── Retención, mensajería y campañas (AF §10) ─────────────────────────────
    "retention.absenceNoticeDays": def({
        scope: "global",
        schema: z.number().int().min(1),
        default: 14,
        rule: "RN-CRM-07",
        editors: "general",
        label: "Días sin venir para el aviso «te extrañamos»",
        description: "Días corridos sin pasar por el molinete tras los que el socio recibe el aviso «te extrañamos».",
        help: "Todas las mañanas el sistema mira la fecha del último ingreso de cada socio con ficha viva (si nunca " +
            "entró, cuenta desde su alta). Cuando ya pasaron estos días enteros sin venir, le manda «te extrañamos» " +
            "por los canales que tenga ese aviso en el catálogo, y lo repite cada día que siga sin venir mientras " +
            "el aviso esté prendido. Es también la cantidad de días que usa el segmento «ausentes» al armar una " +
            "campaña. Con 14, un socio que entró por última vez el 1 recibe el aviso el 16, con dos semanas enteras " +
            "sin venir; con 7, el 9. Conviene que sea menor o igual que los días para entrar en riesgo, así el aviso " +
            "llega antes que el llamado.",
        unit: "días",
    }),
    "retention.riskAfterDays": def({
        scope: "global",
        schema: z.number().int().min(1),
        default: 15,
        rule: "RN-CRM-07",
        editors: "general",
        label: "Días sin venir para entrar en riesgo",
        description: "Días sin ingresos tras los que el socio aparece en «no renovados / en riesgo» con una tarea de contacto.",
        help: "Cuando un socio acumula estos días sin pasar por el molinete, entra al reporte de no renovados / en " +
            "riesgo y se le abre a su sede una tarea de contacto, asignada a alguien de recepción, para que lo " +
            "llamen y anoten el resultado en su ficha. La tarea se crea una sola vez: mientras esté pendiente no se " +
            "duplica, y el reporte lo sigue mostrando hasta que vuelva a entrar. Con 15, un socio cuyo último " +
            "ingreso fue el 1 aparece en el reporte y en la bandeja el 16; con 30, recién al mes. Subilo si el " +
            "equipo no llega a llamar a todos; bajalo si querés reaccionar antes de que deje de pagar.",
        unit: "días",
    }),
    "retention.planExpiringDays": def({
        scope: "global",
        schema: z.number().int().min(1),
        default: 7,
        rule: "RN-CRM-08",
        pv: "PV-84",
        editors: "general",
        label: "Anticipación del segmento «plan por vencer»",
        description: "Días hacia adelante que abarca el segmento «plan por vencer» al armar una campaña.",
        help: "Cuando armás una campaña con el segmento «plan por vencer», el sistema junta a los socios con plan " +
            "activo cuyo próximo vencimiento cae dentro de estos días contados desde hoy. Con 7, una campaña armada " +
            "el 10 le llega a quienes vencen entre el 10 y el 17; con 15, hasta el 25. Es la anticipación que se " +
            "propone por defecto: al crear la campaña se puede indicar otra cantidad de días sólo para ese envío. " +
            "Subilo si querés avisar la renovación con más tiempo; bajalo si el mensaje llega tan temprano que el " +
            "socio lo olvida.",
        unit: "días",
    }),
    "campaigns.measureWindowDays": def({
        scope: "global",
        schema: z.number().int().min(1),
        default: 30,
        rule: "RN-CRM-08",
        editors: "general",
        label: "Días para medir el resultado de una campaña",
        description: "Días después del envío durante los que se cuenta si el socio renovó o volvió al club.",
        help: "Después de mandar una campaña, el sistema sigue a sus destinatarios durante estos días: cuenta como " +
            "«renovó» a quien registró un pago y como «volvió» a quien pasó por el molinete dentro del plazo, " +
            "además de cuántos leyeron el aviso. Con 30, una campaña enviada el 1 de junio suma resultados hasta el " +
            "1 de julio; un pago del 5 de julio ya no cuenta. Subirlo le atribuye a la campaña cosas que quizá " +
            "hubieran pasado igual; bajarlo deja afuera las renovaciones tardías. El conteo se actualiza cada " +
            "madrugada mientras la campaña esté dentro de la ventana.",
        unit: "días",
    }),
    "messaging.autoReplyText": def({
        scope: "global",
        schema: z.string(),
        default: "Gracias por escribirnos. En este momento la recepción está cerrada; te respondemos " +
            "apenas abrimos. Nuestro horario está en la ficha de la sede.",
        rule: "RN-CRM-16",
        pv: "PV-84",
        editors: "general",
        label: "Respuesta automática fuera del horario",
        description: "Mensaje que recibe el socio si escribe a la sede cuando la recepción está cerrada.",
        help: "Cuando un socio manda un mensaje desde la app y la recepción de esa sede está cerrada según su " +
            "horario, el sistema le contesta al instante con este texto y, debajo, agrega solo los horarios de la " +
            "sede (lunes a viernes, sábados y domingos), así que no hace falta escribirlos acá. La conversación " +
            "sigue figurando como «sin responder»: la respuesta automática no reemplaza a la de la sede. Es un " +
            "texto libre y el mismo para todas las sedes; lo que cambia por sede es el horario que va abajo. " +
            "Conviene que diga que se responde al abrir y no prometa plazos que la recepción no pueda cumplir.",
    }),
    "messaging.lockMinutes": def({
        scope: "global",
        schema: z.number().int().min(1).max(60),
        default: 2,
        rule: "RN-CRM-16",
        editors: "general",
        label: "Minutos que dura «lo está respondiendo X»",
        description: "Cuánto dura la marca «lo está respondiendo X» cuando alguien abre una conversación de la bandeja.",
        help: "Cuando alguien de recepción abre una conversación de la bandeja, el resto ve la marca «lo está " +
            "respondiendo X» durante estos minutos, para que dos personas no contesten lo mismo. Es un aviso, no " +
            "una traba: si igual dos responden, los dos mensajes salen y quedan registrados con su autor. Con 2, si " +
            "Ana abre un mensaje y se distrae, a los dos minutos la marca se cae y otra compañera puede tomarlo. " +
            "Subilo si las respuestas suelen ser largas; bajalo si la marca deja conversaciones «tomadas» que nadie " +
            "está contestando. Se admite de 1 a 60.",
        unit: "minutos",
    }),
    "posts.priorModeration": def({
        scope: "global",
        schema: z.boolean(),
        default: true,
        rule: "RN-CRM-13",
        pv: "PV-80",
        editors: "general",
        label: "Aprobar publicaciones del muro antes de verse",
        description: "Prendido, lo que publica un socio se ve recién cuando un moderador lo aprueba; apagado, sale al instante.",
        help: "Define qué pasa cuando un socio publica en el muro social de la app. Prendido, la publicación entra a " +
            "la cola de moderación y nadie la ve hasta que un Dueño o coordinador la aprueba; apagado, se ve al " +
            "instante y los moderadores pueden ocultarla o eliminarla después, y cualquier socio puede denunciarla " +
            "(una denuncia la oculta enseguida). Prendido es más seguro pero exige que alguien revise la cola todos " +
            "los días; apagado hace el muro más vivo, con el riesgo de que algo inapropiado esté visible un rato. " +
            "Cambiarlo afecta sólo lo que se publique de ahí en adelante.",
    }),
    // ── Alertas de seguridad (AF §11.5) ───────────────────────────────────────
    "alerts.security.recipients": def({
        scope: "global",
        schema: z.array(z.string()).min(1),
        default: ["owner", "admin"],
        rule: "RN-ROL-15",
        pv: "PV-78",
        editors: "general",
        label: "Destinatarios por defecto de las alertas de seguridad",
        description: "Quiénes reciben una alerta de seguridad cuando esa alerta no tiene destinatarios propios.",
        help: "Las alertas de seguridad (anulación de un cobro, descuento alto, diferencia de caja, cambio de rol y " +
            "demás) le avisan a las personas cargadas en cada alerta del catálogo; si una alerta quedó sin " +
            "destinatarios, se usan los de acá. Es una lista: cada elemento es un rol (owner = Dueño, admin = " +
            "Administración, intersede_coordinator = Coordinador intersedes, branch_manager = Encargado de sede) o " +
            "el identificador de un usuario puntual, y la reciben sólo los usuarios activos con ese rol. Con " +
            "«owner» y «admin», le llega al Dueño y a todas las cuentas de Administración. Si ponés roles sin " +
            "ningún usuario activo, la alerta no le llega a nadie.",
    }),
    "alerts.security.discountPct": def({
        scope: "global",
        schema: z.number().min(0).max(100),
        default: 10,
        rule: "RN-ROL-15",
        pv: "PV-78",
        editors: "owner_only",
        label: "Descuento a partir del cual se alerta",
        description: "Porcentaje de descuento de referencia para la alerta «descuento sobre el umbral».",
        help: "Es el porcentaje que la pantalla de Alertas de seguridad muestra como umbral de referencia para los " +
            "descuentos. Ojo: el corte que dispara la alerta de verdad es el umbral cargado en la alerta «descuento " +
            "sobre el umbral» del catálogo de alertas de seguridad (10 de fábrica); si cambiás este valor y no " +
            "aquel, la alerta sigue saltando con el de siempre. Con 10, un descuento del 10 % o más en un cobro le " +
            "avisa a los destinatarios en el momento; uno del 9 % no. Sólo el Dueño puede cambiarlo, y conviene " +
            "cambiar los dos juntos.",
        unit: "%",
    }),
    "alerts.security.cashDifferenceAmount": def({
        scope: "global",
        schema: moneyInt.min(0),
        default: 1_000_000, // $10.000,00
        rule: "RN-ROL-15",
        pv: "PV-78",
        editors: "owner_only",
        label: "Diferencia de caja que dispara la alerta",
        description: "Importe de referencia para la alerta «diferencia de caja»; se carga en centavos (1000000 = $10.000).",
        help: "Es el importe que la pantalla de Alertas de seguridad muestra como umbral de referencia para las " +
            "diferencias de caja. Ojo: el corte que dispara la alerta de verdad es el umbral cargado en la alerta " +
            "«diferencia de caja» del catálogo de alertas de seguridad; si cambiás este valor y no aquel, la alerta " +
            "sigue saltando con el de siempre. Se carga en centavos: 1000000 son $10.000,00 y 500000 son " +
            "$5.000,00. Con $10.000, un cierre con $10.000 o más de diferencia (de más o de menos) avisa en el " +
            "momento; uno con $9.500 no. Sólo el Dueño puede cambiarlo, y conviene cambiar los dos juntos.",
        unit: "$",
    }),
    /*
     * `alerts.slack.enabled` y `alerts.slack.webhooks` (URLs de Slack en claro, por categoría)
     * se fueron de acá el 22-oct-2026: cada aviso saliente es un webhook del catálogo de
     * `webhooks.ts`, con su URL cifrada y su switch en Ajustes → Integraciones → Webhooks.
     * Los valores viejos se migran solos (`backend/src/lib/webhooks/config.ts`).
     */
    "alerts.security.catalog": def({
        scope: "global",
        schema: z.record(z.string(), securityAlertEntry),
        // RN-ROL-15: defaults de fábrica — todas activas, a Dueño y Administración.
        default: {
            payment_reversed: {
                active: true,
                threshold: null,
                recipients: ["owner", "admin"],
                channels: ["push", "email"],
            },
            sale_voided: {
                active: true,
                threshold: null,
                recipients: ["owner", "admin"],
                channels: ["push", "email"],
            },
            discount_over_threshold: {
                active: true,
                threshold: 10,
                recipients: ["owner", "admin"],
                channels: ["push", "email"],
            },
            cash_difference_over_threshold: {
                active: true,
                threshold: 1_000_000,
                recipients: ["owner", "admin"],
                channels: ["push", "email"],
            },
            payment_corrected: {
                active: true,
                threshold: null,
                recipients: ["owner", "admin"],
                channels: ["push", "email"],
            },
            access_exception_granted: {
                active: true,
                threshold: null,
                recipients: ["owner", "admin"],
                channels: ["push", "email"],
            },
            price_list_changed: {
                active: true,
                threshold: null,
                recipients: ["owner", "admin"],
                channels: ["push", "email"],
            },
            role_changed: {
                active: true,
                threshold: null,
                recipients: ["owner", "admin"],
                channels: ["push", "email"],
            },
            user_created_or_deactivated: {
                active: true,
                threshold: null,
                recipients: ["owner", "admin"],
                channels: ["push", "email"],
            },
            mass_export: {
                active: true,
                threshold: null,
                recipients: ["owner", "admin"],
                channels: ["push", "email"],
            },
            special_freeze: {
                active: true,
                threshold: null,
                recipients: ["owner", "admin"],
                channels: ["push", "email"],
            },
            failed_logins: {
                active: true,
                threshold: 5,
                recipients: ["owner", "admin"],
                channels: ["push", "email"],
            },
            /*
             * Cambio de credenciales de una integración con un tercero (Payway, ARCA,
             * Interbanking, MP, GSD, email, push, S3). Es la extensión natural de «cambio de rol
             * o permisos» de RN-ROL-14: quien toca la api key de cobro o el certificado fiscal
             * puede cobrar y facturar a nombre del club. La alerta lleva QUÉ campos cambiaron,
             * nunca sus valores.
             */
            integration_credentials_changed: {
                active: true,
                threshold: null,
                recipients: ["owner", "admin"],
                channels: ["push", "email"],
            },
        },
        rule: "RN-ROL-14 / RN-ROL-15",
        pv: "PV-78",
        editors: "general",
        label: "Catálogo de alertas de seguridad",
        description: "Cada alerta de seguridad: si está activa, su umbral, a quiénes avisa y por qué canal.",
        help: "Es la lista de hechos sensibles que avisan en el momento en que ocurren: anulación de un cobro o de " +
            "una venta, descuento sobre el umbral, diferencia de caja, corrección de un pago, ingreso autorizado con " +
            "deuda, cambio en la lista de precios, cambio de rol, alta o baja de usuario, exportación masiva, " +
            "congelamiento especial, bloqueo por intentos de ingreso fallidos y cambio de las credenciales de una " +
            "integración. Cada alerta tiene «active» (apagada, no avisa), «threshold» (el umbral: porcentaje en el " +
            "descuento, centavos en la diferencia de caja, cantidad de intentos en los ingresos fallidos; null en " +
            "las que no tienen), «recipients» (roles como owner o admin, o identificadores de usuarios; vacío usa " +
            "los destinatarios por defecto) y «channels» (push, email y/o centro de avisos). Por ejemplo, con " +
            "threshold 1000000 en «cash_difference_over_threshold», una diferencia de $10.000 o más avisa y una de " +
            "$9.000 no. Lo cómodo es tocarlo alerta por alerta desde la pantalla de Alertas de seguridad, donde los " +
            "umbrales los cambia sólo el Dueño; acá se carga como texto estructurado (JSON) completo.",
    }),
    // ── Alertas de negocio (AF §12) ───────────────────────────────────────────
    "alerts.business.enabled": def({
        scope: "global",
        schema: z.boolean(),
        default: true,
        rule: "RN-REP-13",
        editors: "general",
        label: "Alertas de caída de asistencia y facturación",
        description: "Interruptor general de las alertas de negocio: caída de asistencia y de facturación por sede.",
        help: "Prendido, cada noche a las 21:10 el sistema compara la asistencia y la facturación del día de cada " +
            "sede contra el promedio de ese mismo día de semana en las últimas semanas y, si la caída llega al " +
            "porcentaje configurado, avisa a los destinatarios. Apagado, no se evalúa nada ni queda historial de " +
            "disparos; los umbrales y destinatarios siguen cargados para cuando lo vuelvas a prender. Los feriados " +
            "y los días en que la sede no abre no se evalúan nunca, esté prendido o no. Conviene apagarlo sólo en " +
            "períodos atípicos (una sede en obra, un mes de vacaciones) en los que la caída es esperable y el aviso " +
            "sería ruido.",
    }),
    "alerts.business.attendanceDropPct": def({
        scope: "global",
        schema: z.number().min(1).max(100),
        default: 20,
        rule: "RN-REP-11",
        pv: "PV-81",
        editors: "general",
        label: "Caída de asistencia que dispara la alerta",
        description: "Porcentaje de caída de los asistentes del día, contra el promedio, a partir del cual se avisa.",
        help: "Cada noche el sistema toma la asistencia del día de cada sede y la compara con el promedio del mismo " +
            "día de semana en las semanas anteriores. Si la caída llega a este porcentaje o lo supera, manda la " +
            "alerta «caída de asistencia» con el número del día y el promedio. Con 20, una sede que promedia 150 " +
            "asistentes los martes avisa si un martes entran 120 o menos; con 30, recién con 105 o menos. Bajalo " +
            "si querés enterarte de caídas chicas; subilo si la asistencia varía mucho y la alerta salta demasiado " +
            "seguido. Se admite de 1 a 100.",
        unit: "%",
    }),
    "alerts.business.revenueDropPct": def({
        scope: "global",
        schema: z.number().min(1).max(100),
        default: 20,
        rule: "RN-REP-12",
        pv: "PV-81",
        editors: "general",
        label: "Caída de facturación que dispara la alerta",
        description: "Porcentaje de caída de la facturación del día, contra el promedio, a partir del cual se avisa.",
        help: "Cada noche el sistema toma la facturación del día de cada sede y la compara con el promedio del mismo " +
            "día de semana en las semanas anteriores. Si la caída llega a este porcentaje o lo supera, manda la " +
            "alerta «caída de facturación» con el importe del día y el promedio a los destinatarios de facturación. " +
            "Con 20, una sede que promedia $500.000 los lunes avisa si un lunes factura $400.000 o menos; con 40, " +
            "recién con $300.000 o menos. Desde la pantalla de Alertas de negocio este umbral lo cambia sólo el " +
            "Dueño; los feriados y los días en que la sede no abre no se evalúan.",
        unit: "%",
    }),
    "alerts.business.compareWeeks": def({
        scope: "global",
        schema: z.number().int().min(1),
        default: 4,
        rule: "RN-REP-11 / RN-REP-12",
        pv: "PV-81",
        editors: "general",
        label: "Semanas de referencia para el promedio",
        description: "Cuántos mismos días de semana anteriores se promedian para detectar una caída.",
        help: "Para saber si un día cayó, el sistema arma el promedio con los mismos días de semana anteriores: con " +
            "4, un martes se compara contra los cuatro martes previos. Los feriados y los días en que la sede no " +
            "abrió se saltean y se busca más atrás (hasta el triple de semanas) para completar la ventana; si no " +
            "junta al menos dos días comparables, no avisa y deja anotado por qué. Con 4 la referencia reacciona " +
            "rápido a un cambio de temporada; con 8 es más estable pero tarda más en reflejar que el club creció o " +
            "se achicó. Aplica tanto a asistencia como a facturación.",
        unit: "semanas",
    }),
    "alerts.business.recipients": def({
        scope: "global",
        schema: z.array(z.string()).min(1),
        default: ["owner"],
        rule: "RN-REP-11 / RN-REP-12",
        editors: "general",
        label: "Destinatarios de la alerta de asistencia",
        description: "Quiénes reciben la alerta de caída de asistencia (roles o usuarios puntuales).",
        help: "Lista de quiénes reciben el aviso «caída de asistencia», por push y en el centro de avisos. Cada " +
            "elemento es un rol (owner = Dueño, admin = Administración, intersede_coordinator = Coordinador " +
            "intersedes, branch_manager = Encargado de sede) o el identificador de un usuario puntual, y lo reciben " +
            "sólo los usuarios activos con ese rol. Con «owner» e «intersede_coordinator», le llega al Dueño y a la " +
            "coordinación; si la lista queda sin usuarios activos, la alerta no sale. La alerta de facturación tiene " +
            "su propia lista, porque no todos los roles pueden ver facturación.",
    }),
    "reports.dailyDigestHour": def({
        scope: "global",
        schema: z.number().int().min(0).max(23),
        default: 21,
        rule: "RN-REP-01",
        pv: "PV-76",
        editors: "general",
        label: "Hora del resumen diario al Dueño",
        description: "Hora en que sale el aviso diario con facturación, asistentes y ventas contra objetivo, " +
            "acumulados hasta ahí.",
        help: "Todos los días, a esta hora en punto, el Dueño recibe en el celular los tres números " +
            "del día: facturación (total y por sede, por medio de pago), asistentes y ventas o " +
            "renovaciones contra el objetivo del mes, acumulados hasta ese momento; lo que se cobre " +
            "después queda para el dashboard del día siguiente, que muestra el día completo. Se " +
            "carga como hora entera de 0 a 23: 21 es las nueve de la noche y 22, las diez. El " +
            "cambio rige desde la próxima hora en punto. Conviene una hora en que las sedes ya " +
            "cerraron la caja de la tarde: más temprano, el número del día llega incompleto.",
        unit: "hora (0 a 23)",
    }),
    "alerts.business.revenueRecipients": def({
        scope: "global",
        schema: z.array(z.string()).min(1),
        default: ["owner"],
        rule: "RN-REP-12 / RN-ROL-02",
        editors: "owner_only",
        label: "Destinatarios de la alerta de facturación",
        description: "Quiénes reciben la alerta de caída de facturación; va aparte porque no todos pueden ver facturación.",
        help: "Lista de quiénes reciben el aviso «caída de facturación», con el importe del día y el promedio, por " +
            "push y en el centro de avisos. Va separada de la de asistencia porque no todos los roles pueden ver " +
            "facturación: mandársela a quien no la ve sería mostrarle por un aviso un número que el sistema le " +
            "oculta en pantalla. Cada elemento es un rol (owner = Dueño, admin = Administración) o el " +
            "identificador de un usuario puntual; con «owner» le llega sólo al Dueño. Sólo el Dueño puede cambiar " +
            "esta lista.",
    }),
    // ── Reportes (AF §12) ─────────────────────────────────────────────────────
    "reports.atRiskDays": def({
        scope: "global",
        schema: z.number().int().min(1).max(365),
        // rel. 13.6: «a los 15 días sin venir consideran que un socio se está por ir».
        default: 15,
        rule: "RN-REP-08",
        editors: "general",
        label: "Días sin venir para marcar riesgo",
        description: "Días sin ingresar al club a partir de los cuales un socio entra en el reporte de no renovados / en riesgo.",
        help: "El reporte No renovados / en riesgo lista a los socios activos o morosos que no registran un ingreso " +
            "permitido en los últimos N días (quien nunca vino también cuenta) o que tienen una cuota vencida; este " +
            "parámetro es el N. Con 15, un socio cuyo último ingreso fue hace 16 días aparece; con 30, recién aparece " +
            "a los 30. Bajarlo agranda la lista y hace que la recepción contacte a más gente y más temprano; subirlo " +
            "la acorta, pero se llega más tarde a quien se está yendo. La cuota vencida lo mete en la lista igual, sin " +
            "importar este número, y desde la pantalla del reporte se puede probar otro valor para una consulta " +
            "puntual sin cambiar el parámetro.",
        unit: "días",
    }),
    "reports.centralAllocation": def({
        scope: "global",
        schema: z.object({
            mode: z.enum(["by_revenue", "equal", "manual"]),
            /** Sólo en `manual`: peso por sede; se normaliza al 100 % al aplicarlo. */
            weights: z.record(z.string(), z.number().min(0)),
        }),
        default: {
            mode: "by_revenue",
            weights: {},
        },
        rule: "RN-CON-17",
        pv: "PV-73",
        // AF §9.5, pantalla «Resultado por sede»: el coeficiente lo ajustan los dueños.
        editors: "owner_only",
        label: "Reparto de gastos de Central entre sedes",
        description: "Criterio para repartir los gastos de la sede virtual Central (contador, impuestos, administración).",
        help: "Los gastos que no son de ninguna sede (contador, impuestos generales, sueldos de administración) se " +
            "cargan a la sede virtual Central, y el Resultado por sede se los reparte a las tres sedes mostrándolos " +
            "aparte de los gastos propios de cada una. mode define el criterio: by_revenue reparte en proporción a " +
            "la facturación de cada sede en ese mes (una sede que factura el 50 % del club carga con el 50 % de " +
            "Central), equal reparte en partes iguales, y manual usa los pesos de weights, uno por sede con el " +
            'identificador de la sede como clave (por ejemplo { "<id de Olivos>": 2, "<id de La Lucila>": 1, ' +
            '"<id de Martínez>": 1 } reparte 50/25/25; los pesos se normalizan solos). Si con by_revenue no hubo ' +
            "ingresos en el mes, o con manual todos los pesos están en cero, cae a partes iguales y el reporte lo " +
            "avisa. Se ajusta desde Resultado por sede y lo decide el Dueño.",
    }),
    "holidays.calendar": def({
        scope: "global",
        schema: z.record(z.string().regex(/^\d{4}$/), z.array(z.string().regex(/^\d{4}-\d{2}-\d{2}$/))),
        // Sin calendario cargado NINGÚN día es feriado: un `false` honesto, no adivinado
        // (`lib/dates.ts → isHoliday`).
        default: {},
        rule: "RN-PRO-05 / RN-CLA-10 / RN-REP-11",
        editors: "holiday_calendar",
        label: "Feriados para las alertas de negocio",
        description: "Fechas feriadas por año que las alertas de caída de asistencia y facturación no evalúan ni " +
            "usan de base.",
        help: "Las alertas de negocio comparan la asistencia y la facturación de cada día de cada sede " +
            "contra el promedio de ese mismo día de semana en las semanas anteriores. Un feriado tiene " +
            "menos gente por naturaleza: si la fecha figura acá, ese día no se evalúa (la caída es " +
            "esperable y no dispara la alerta) y tampoco se usa como base para comparar los días " +
            "siguientes; si además la sede no abre los feriados, el día se saltea. Se carga por año con " +
            'la lista de fechas: {"2026": ["2026-01-01", "2026-05-01"]}. Sin nada cargado, ningún día ' +
            "se trata como feriado y la caída de un 25 de mayo dispararía la alerta. Hoy este " +
            "calendario lo leen sólo las alertas de negocio: el que usan la grilla de clases, el pago " +
            "de la hora de los profesores y los días hábiles de caja es el que se carga en Personal → " +
            "Tarifario, pestaña Feriados.",
    }),
    // ── Sesiones y seguridad (AF §11.6) ───────────────────────────────────────
    "session.portalIdleHours": def({
        scope: "global",
        schema: z.number().int().min(1),
        default: 8,
        rule: "RN-ROL-16",
        pv: "PV-83",
        editors: "general",
        label: "Horas de inactividad para cerrar el portal",
        description: "Horas sin actividad tras las que se cierra la sesión en el portal de administración y " +
            "en el de profesores.",
        help: "Cada acción en el portal renueva la sesión; si pasa más tiempo que este valor sin " +
            "ninguna, la sesión se cierra y hay que volver a ingresar con usuario y contraseña. No " +
            "afecta a la tablet kiosko ni a la app de socios, que tienen sus propias reglas. Con 8, " +
            "quien dejó el portal abierto al irse a las 18 lo encuentra cerrado a la mañana " +
            "siguiente; con 1, una computadora del mostrador que quedó sola una hora pide la clave " +
            "de nuevo. Bajarlo protege más las computadoras compartidas; subirlo evita reingresos a " +
            "quien trabaja todo el día en el portal.",
        unit: "horas",
    }),
    "session.appRefreshDays": def({
        scope: "global",
        schema: z.number().int().min(1),
        default: 90,
        rule: "RN-ROL-16",
        pv: "PV-83",
        editors: "general",
        label: "Duración de la sesión en la app",
        description: "Días que la app de socios mantiene la sesión abierta sin volver a pedir el ingreso.",
        help: "La app del socio no pide la clave cada vez: guarda una sesión larga que se renueva sola " +
            "mientras la usa. Este valor es cuánto dura esa sesión desde la última renovación; si el " +
            "socio no abre la app en todo ese tiempo, la próxima vez tiene que ingresar de nuevo. " +
            "Con 90, quien no entra a la app en tres meses vuelve a ver la pantalla de ingreso; con " +
            "365, prácticamente nunca. Rige para las sesiones que se abran o renueven desde el " +
            "cambio. Bajarlo hace que los socios que usan poco la app tengan que ingresar más " +
            "seguido.",
        unit: "días",
    }),
    "session.loginMaxAttempts": def({
        scope: "global",
        schema: z.number().int().min(1),
        default: 5,
        rule: "RN-ROL-16",
        editors: "general",
        label: "Intentos fallidos de ingreso antes del bloqueo",
        description: "Contraseñas incorrectas seguidas tras las que un usuario del personal queda bloqueado.",
        help: "Cada contraseña incorrecta suma un intento para ese usuario; al llegar a este número, " +
            "el usuario queda bloqueado por los minutos del parámetro de bloqueo y se dispara una " +
            "alerta de seguridad. Sólo un ingreso correcto vuelve el contador a cero. Con 5, la " +
            "quinta contraseña equivocada seguida bloquea; con 3, la tercera. Bajarlo frena antes a " +
            "quien prueba claves ajenas, pero bloquea más seguido a quien se equivoca al tipear. " +
            "Aplica a los usuarios del personal, no al ingreso de los socios en la app.",
        unit: "intentos",
    }),
    "session.lockMinutes": def({
        scope: "global",
        schema: z.number().int().min(1),
        default: 15,
        rule: "RN-ROL-16",
        editors: "general",
        label: "Minutos de bloqueo por intentos fallidos",
        description: "Cuánto tiempo queda bloqueado un usuario del personal tras agotar los intentos de ingreso.",
        help: "Cuando un usuario agota los intentos fallidos, no puede ingresar durante estos minutos " +
            "aunque escriba la contraseña correcta; la pantalla le dice cuántos minutos le faltan. " +
            "Pasado el tiempo puede volver a intentar, pero el contador de intentos no se reinicia: " +
            "una sola contraseña incorrecta más lo bloquea de nuevo, y recién un ingreso correcto lo " +
            "pone en cero. Con 15, quien se bloqueó a las 10:00 puede volver a probar a las 10:15; " +
            "con 60, recién a las 11:00. Subirlo frena más a quien prueba claves ajenas, pero deja " +
            "más tiempo sin poder trabajar a quien se equivocó de verdad.",
        unit: "minutos",
    }),
    "session.twoFactorRequiredRoles": def({
        scope: "global",
        schema: z.array(z.string()),
        default: ["owner", "admin"],
        rule: "RN-ROL-17",
        pv: "PV-83",
        editors: "general",
        label: "Roles con código por mail al ingresar",
        description: "Roles del personal que, además de la contraseña, deben escribir un código enviado a su " +
            "mail para entrar.",
        help: 'Se carga como lista entre corchetes con los nombres internos de los roles: ["owner", ' +
            '"admin"] son Dueño y Administración; los demás son "intersede_coordinator" (Coordinador ' +
            'intersedes), "branch_manager" (Encargado de sede), "receptionist" (Recepcionista), ' +
            '"teacher" (Profesor) y "accountant_readonly" (Contador, sólo lectura). A quien tiene ' +
            "uno de estos roles, cada ingreso al portal le manda a su mail un código de un solo uso " +
            "que vence a los 5 minutos y hay que escribir para terminar de entrar. Con [] nadie lo " +
            "recibe por su rol, aunque un usuario puntual puede tener el código activado en su " +
            'propia ficha. Agregar "branch_manager" se lo exige también a los encargados. Protege a ' +
            "quienes ven la facturación completa; exigírselo a recepción vuelve más lento cada " +
            "cambio de turno.",
    }),
    // ── Migración y puesta en marcha (AF §13) ─────────────────────────────────
    "migration.archiveAfterMonths": def({
        scope: "global",
        schema: z.number().int().min(1),
        default: 6,
        rule: "RN-MIG-01",
        pv: "PV-85",
        editors: "general",
        label: "Meses de inactividad para migrar como archivo",
        description: "Inactivos del sistema viejo con más de estos meses sin asistir se migran como archivo, no como socios.",
        help: "Al migrar el padrón de Crossfy, los socios activos y morosos entran como tales; los inactivos se " +
            "dividen según su última asistencia respecto de la fecha de corte de la migración: si pasaron más de " +
            "estos meses, entran como archivo histórico, que se puede buscar por DNI o nombre para reincorporarlos " +
            "pero nunca cuenta como socio ni aparece en las listas; si pasaron menos, entran como inactivos comunes. " +
            "Con 6, alguien que vino por última vez hace 8 meses va al archivo y alguien de hace 4 queda inactivo. " +
            "Un inactivo sin fecha de última asistencia no se archiva por las dudas: queda inactivo y la validación " +
            "lo lista. Subirlo trae más gente al padrón de inactivos; bajarlo manda más gente al archivo. Sólo " +
            "afecta las corridas de migración que se hagan de ahí en adelante, no lo ya cargado.",
        unit: "meses",
    }),
    "migration.testValidityDays": def({
        scope: "global",
        schema: z.number().int().min(1),
        default: 30,
        rule: "RN-MIG-09",
        editors: "general",
        label: "Vigencia de una migración de prueba",
        description: "Días durante los cuales una migración de prueba sigue valiendo para habilitar el corte.",
        help: "Antes del corte hacen falta al menos dos migraciones de prueba completas, sin errores abiertos y " +
            "vigentes. Cada prueba vence a los N días de ejecutada: pasado ese plazo ya no cuenta, porque el padrón " +
            "del sistema viejo siguió moviéndose y lo que se validó ya no es lo que se va a migrar. Con 30, una " +
            "prueba corrida el 1 de marzo deja de servir el 31 de marzo; si el corte se agenda para abril, hay que " +
            "repetirla. Subirlo permite agendar el corte con pruebas más viejas; bajarlo obliga a probar más cerca de " +
            "la fecha, que es lo prudente. Aplica a las pruebas que se ejecuten desde el cambio en adelante.",
        unit: "días",
    }),
    "migration.rolloutWindowDays": def({
        scope: "global",
        schema: z.number().int().min(1),
        default: 14,
        rule: "RN-MIG-11",
        pv: "PV-86",
        editors: "general",
        label: "Ventana del encendido por sede",
        description: "Días previstos para encender la operación de cada sede después del piloto; es una meta, no un bloqueo.",
        help: "El corte de datos y cobranza es único para las tres sedes, pero molinete/QR, reservas por app y kiosko " +
            "se encienden sede por sede, empezando por Olivos como piloto. Este número es la ventana objetivo del " +
            "escalonamiento: cuántos días se le da a cada sede antes de sumar la siguiente. Hoy sólo se muestra en " +
            "la pantalla Corte y encendido por sede como referencia del plan: no bloquea ni dispara nada, el " +
            "encendido lo hacés a mano con los interruptores de cada sede. Con 14, el plan es Olivos dos semanas, " +
            "después La Lucila dos semanas, después Martínez.",
        unit: "días",
    }),
    "migration.cutoffDate": def({
        scope: "global",
        schema: z
            .string()
            .regex(/^\d{4}-\d{2}-\d{2}$/)
            .nullable(),
        default: null,
        rule: "RN-MIG-08",
        editors: "owner_only",
        label: "Fecha de corte de la migración",
        description: "Día acordado para el corte único de datos y cobranza. Vacío (null) = todavía no hay fecha agendada.",
        help: "Es la fecha en que se hace el export final de los sistemas viejos, se migra lo que cambió desde la " +
            "prueba y el sistema nuevo pasa a ser la verdad para las tres sedes a la vez. La fija el Dueño desde " +
            "Corte y encendido por sede al habilitar el corte, y esa pantalla sólo lo permite con el checklist de " +
            "puesta en marcha completo; por eso no conviene cargarla a mano acá, porque saltearía ese control. Se " +
            "escribe como año-mes-día, por ejemplo 2026-10-01. Mientras no hay fecha, el valor se ve como null, " +
            "que quiere decir «sin agendar».",
    }),
    "migration.legacyFrozenAt": def({
        scope: "global",
        schema: z.string().nullable(),
        default: null,
        rule: "RN-MIG-08",
        editors: "owner_only",
        label: "Momento en que se congelaron los sistemas viejos",
        description: "Fecha y hora desde la que Crossfy y Control Gym quedaron en sólo lectura. Vacío (null) = el corte no arrancó.",
        help: "Marca el instante en que Crossfy y Control Gym dejaron de ser la verdad: desde ahí no se carga nada más " +
            "en ellos y toda la operación pasa al sistema nuevo. Lo escribe el botón de congelar los sistemas viejos " +
            "de Corte y encendido por sede, que exige el corte habilitado y la migración delta final ya ejecutada " +
            "(congelar antes perdería los pagos de la última noche). Mientras esté en null, el sistema entiende que " +
            "el corte no arrancó y no deja encender molinete, reservas ni kiosko en ninguna sede. No lo cargues a " +
            "mano: si se completa sin haber migrado, el sistema se cree la verdad con datos que todavía se editan " +
            "en el viejo.",
    }),
    "migration.cutoverEnabledAt": def({
        scope: "global",
        schema: z.string().nullable(),
        default: null,
        rule: "RN-MIG-16",
        editors: "owner_only",
        label: "Momento en que se habilitó el corte",
        description: "Fecha y hora en que el checklist quedó completo y el Dueño habilitó el corte. Vacío (null) = no habilitado.",
        help: "El corte se habilita una sola vez, desde Corte y encendido por sede, cuando todos los ítems del " +
            "checklist de puesta en marcha están aprobados; en ese momento el sistema guarda acá la fecha y hora, " +
            "junto con la fecha de corte. Mientras esté en null, el botón de congelar los sistemas viejos se rechaza " +
            "con el aviso de que falta completar el checklist. Es un registro que escribe el sistema: completarlo a " +
            "mano equivale a declarar el checklist completo sin serlo, y vaciarlo vuelve a bloquear el congelamiento.",
    }),
    "branch.liveFeatures": def({
        scope: "branch",
        schema: z.object({
            access: z.boolean(),
            bookings: z.boolean(),
            kiosk: z.boolean(),
        }),
        default: { access: false, bookings: false, kiosk: false },
        rule: "RN-MIG-11",
        pv: "PV-86",
        editors: "general",
        label: "Funciones encendidas en la sede",
        description: "Interruptores por sede: molinete/QR (access), reservas por app (bookings) y kiosko (kiosk). Nacen apagados.",
        help: "Después del corte, cada sede se enciende por separado y de a partes: access es el control de ingreso " +
            "por molinete y QR, bookings las reservas de clases desde la app y kiosk la tablet de autopago y punto " +
            "de venta. Cada uno vale true (encendido) o false (apagado), y toda sede nace con los tres apagados. Se " +
            "opera desde Corte y encendido por sede con motivo obligatorio: prender exige que los sistemas viejos ya " +
            "estén congelados (un molinete decidiendo con datos que todavía se editan en Crossfy dejaría entrar a " +
            "quien no debe); apagar no exige nada, si algo sale mal en el piloto se apaga y listo. La caja de la " +
            "sede no depende de esto: está en el sistema nuevo desde el corte, tenga o no encendido el resto.",
    }),
    "backup.opsEmails": def({
        scope: "global",
        schema: z.array(z.string()),
        default: [],
        rule: "Arquitectura §9.2",
        editors: "general",
        label: "Casillas de operación para avisos de infraestructura",
        description: "Correos del equipo que mantiene el sistema (Deepyze) que reciben el resultado de la prueba mensual de restore.",
        help: 'Lista de direcciones de correo, entre corchetes y con comillas (["ops@deepyze.dev"]), a las que se ' +
            "les manda por mail el resultado de cada prueba de restauración del backup: anduvo, falló o no se pudo " +
            "probar. No son casillas del club: el Dueño y Administración reciben ese mismo aviso por la campana del " +
            "portal (y por mail y celular cuando hay un problema) sin figurar acá; estas son las de quien atiende la " +
            "infraestructura, que es parte del mantenimiento. Vacía, nadie de operación recibe el mail y el aviso " +
            "queda sólo en el portal. Cambiala si cambia quien monitorea el servidor.",
    }),
    // ── Backups y prueba de restore (Arquitectura §9.1) ───────────────────────
    "backup.lastRestoreCheck": def({
        scope: "global",
        schema: z
            .object({
            at: z.string(),
            ok: z.boolean().nullable(),
            dump: z.string().nullable(),
            durationMs: z.number().int().min(0).nullable(),
            counts: z.record(z.string(), z.number().int().min(0)),
            issues: z.array(z.string()),
            reason: z.string().nullable(),
        })
            .nullable(),
        default: null,
        rule: "Arquitectura §9.1",
        editors: "general",
        label: "Resultado de la última prueba de restore",
        description: "Lo que dejó la última restauración de prueba del backup. Vacío (null) = nunca se probó, que no es fallar.",
        help: "Lo escribe el sistema, no una persona: el día 2 de cada mes a las 4 de la mañana restaura el último " +
            "backup en una base aparte y verifica que los conteos y las referencias cierren; el resultado queda acá " +
            "y se ve en Salud del sistema, en Backups y prueba de restore. Contiene at (cuándo corrió), ok (true " +
            "anduvo, false falló, null no se pudo probar), dump (qué archivo se restauró), durationMs (cuánto " +
            "tardó, en milisegundos), counts (cuántos socios, pagos, registros de auditoría, etc. se restauraron), " +
            "issues (qué falló) y reason (por qué no se pudo probar). Mientras sea null nunca se probó una " +
            "restauración, y un backup sin prueba de restore no es un backup. No hace falta editarlo: si lo pisás a " +
            "mano, Salud del sistema muestra algo que no pasó.",
    }),
    // ── Capacitación del staff (docs/spec/capacitacion.md) ────────────────────
    "capacitacion.notaAprobacion": def({
        scope: "global",
        schema: z.number().int().min(1).max(100),
        default: 80,
        rule: "RN-CAP-04 / RN-CAP-08",
        editors: "general",
        label: "Nota mínima para aprobar las evaluaciones del curso",
        description: "Puntaje sobre 100 desde el que se aprueba la evaluación de una lección y el examen final.",
        help: "Cada lección del curso de capacitación termina con una evaluación de 4 preguntas, y el " +
            "curso de cada rol cierra con un examen final. Se aprueba con este puntaje o más, sobre " +
            "100. Con 80, en una evaluación de 4 preguntas hay que acertar las 4 (3 de 4 es 75); con " +
            "75, alcanza con 3. Los reintentos son ilimitados y cada intento queda registrado en el " +
            "seguimiento del equipo. Cambiarlo rige para las evaluaciones que se rindan de ahí en " +
            "adelante: lo ya aprobado sigue aprobado.",
        unit: "puntos",
    }),
    "capacitacion.preguntasExamenFinal": def({
        scope: "global",
        schema: z.number().int().min(1).max(332),
        default: 20,
        rule: "RN-CAP-08",
        editors: "general",
        label: "Preguntas del examen final del curso",
        description: "Cuántas preguntas se sortean del banco de las lecciones del rol para el examen final.",
        help: "Cuando una persona completa todas las lecciones de su rol se le habilita el examen " +
            "final: el sistema sortea esta cantidad de preguntas entre las de sus lecciones, en orden " +
            "aleatorio. Si el rol tiene menos preguntas que este número, el examen usa todas las que " +
            "hay. Con 20, el examen de una recepcionista sale de sus 120 preguntas; con más " +
            "preguntas el examen es más largo y más representativo. Aprobarlo emite el certificado " +
            "del rol.",
        unit: "preguntas",
    }),
    "capacitacion.plazoDias": def({
        scope: "global",
        schema: z.number().int().min(1),
        default: 30,
        rule: "RN-CAP-11",
        editors: "general",
        label: "Días para terminar el curso de capacitación",
        description: "Días desde el alta del usuario para terminar su curso. Sólo informa: no bloquea nada.",
        help: "Cuenta desde el día en que se creó el usuario. Pasado el plazo sin el examen final " +
            "aprobado, la persona figura como «vencida» en el seguimiento de la capacitación del " +
            "equipo, para que su encargado la acompañe. No bloquea la caja, los cobros ni ninguna " +
            "otra operación: el curso es seguimiento, no un permiso. Con 30, alguien que entró el 1 " +
            "de marzo figura vencido desde el 31 si todavía no aprobó el final.",
        unit: "días",
    }),
    "capacitacion.videoManifestUrl": def({
        scope: "global",
        schema: z
            .string()
            .url()
            .refine((u) => /^https?:\/\//i.test(u), "La dirección tiene que empezar con http:// o https://")
            .nullable(),
        default: null,
        rule: "RN-CAP-10",
        editors: "general",
        label: "Dirección del listado de videos del curso",
        description: "Dónde publica Deepyze el listado de videos de las lecciones. Vacío (null) = sin videos.",
        help: "Los videos del curso los produce y aloja Deepyze aparte; el sistema los encuentra " +
            "leyendo un listado publicado en esta dirección, que trae el video de cada lección. " +
            "Mientras esté en null, el curso funciona igual, sin videos, y ninguna lección exige " +
            "verlos. Con una dirección cargada, cada lección que tenga video publicado pide verlo " +
            "(al menos el 90 %) para completarse; las que no lo tengan se completan sin él. Si la " +
            "dirección no responde, el curso sigue andando y las lecciones avisan que el video no se " +
            "pudo cargar. La dirección la pasa Deepyze; no la cambies sin hablar con ellos.",
    }),
};
export const SETTING_KEYS = Object.keys(SETTINGS_REGISTRY);
export function isSettingKey(key) {
    return Object.prototype.hasOwnProperty.call(SETTINGS_REGISTRY, key);
}
export function getSettingDefinition(key) {
    return SETTINGS_REGISTRY[key];
}
/** Default del AF para una clave. Es el valor efectivo cuando no hay documento. */
export function defaultSetting(key) {
    return SETTINGS_REGISTRY[key].default;
}
/** Valida un valor contra el esquema de su clave. Lanza `ZodError` si no cierra. */
export function parseSetting(key, value) {
    return SETTINGS_REGISTRY[key].schema.parse(value);
}
/** Claves con `scope: "branch"` — admiten un documento por sede que pisa el global. */
export const BRANCH_SCOPED_KEYS = SETTING_KEYS.filter((k) => SETTINGS_REGISTRY[k].scope === "branch");
/**
 * Familia de claves dinámicas: el switch de cada job.
 * Regla de operación (CLAUDE.md): **todo job automático tiene switch en `settings`**.
 * La clave concreta es `jobs.<nombre>.enabled`; el default es `true`.
 */
export const JOB_SETTING_PREFIX = "jobs.";
export const jobSettingKey = (jobName) => `${JOB_SETTING_PREFIX}${jobName}.enabled`;
export const jobSettingSchema = z.boolean();
export const JOB_SETTING_DEFAULT = true;
export function isJobSettingKey(key) {
    return key.startsWith(JOB_SETTING_PREFIX) && key.endsWith(".enabled");
}
//# sourceMappingURL=settings-registry.js.map